// SSL / TLS — unified page combining the legacy Overview + Configuration
// tabs into a single scannable view. Matches the Anthropic design bundle
// handoff 97nWIa-cTrIIddJ60b3w5g (ssl-tls/index.html).
//
// Reuses existing wiring:
//   - useSSLConfig() → { sslConfig, sslRating, sslTypeChange, configSslSetting }
//   - SSLConfigurationFree / Upload / GenerateOrigin drawer panels for the
//     install-certificate flow (right rail card → slide drawer).
//
// All data is real — sslConfig (PG-backed), sslRating (live SSL Labs
// probe), and the dynamically-derived recommendations list. Nothing
// mocked. Where a data source isn't wired (e.g. cert history, HTTPS
// traffic %) the cell renders "—" honestly rather than fake numbers.

import React from "react";
import {
  Box, Stack, Typography, IconButton, MenuItem, Select,
  Modal, Slide, Tabs, Tab, CircularProgress, Checkbox, ListItemText, TextField, Tooltip,
} from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockIcon from "@mui/icons-material/Lock";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import GppBadIcon from "@mui/icons-material/GppBadOutlined";
import GppGoodIcon from "@mui/icons-material/GppGoodOutlined";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import ActivityIcon from "@mui/icons-material/QueryStats";
import MemoryIcon from "@mui/icons-material/Memory";
import RedoIcon from "@mui/icons-material/Redo";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StarIcon from "@mui/icons-material/StarOutline";
import HistoryIcon from "@mui/icons-material/History";
import PublicIcon from "@mui/icons-material/PublicOutlined";
import KeyIcon from "@mui/icons-material/KeyOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SyncIcon from "@mui/icons-material/Sync";

import colors from "../../../../utils/colors";
import { formatDate } from "../../../../utils/format";
import StickySaveBar from "../../../common/StickySaveBar";
import FormHelpTooltip from "../../../common/FormHelpTooltip";
import {
  certsOverlineSx,
  certsBodySx,
  certsBodySecondarySx,
  certsBodySmallSx,
  certsCardTitleSx,
  certsSectionTitleSx,
  certsStatSx,
  certsPillTextSx,
  certsFieldLabelSx,
  certsGradeDisplaySx,
  certsGridValueSx,
  certsGridMonoValueSx,
  certsRowTitleSx,
} from "./certsTypography";
import {
  CERTS_FAINT_GREY,
  CERTS_PAGE_SECTION_SPACING,
  CERTS_CARD_GRID_GAP,
  CERTS_GRID_GAP,
  CERTS_INNER_STACK_SPACING,
  CERTS_SECTION_HEADER_MB,
  CERTS_CARD_PAD,
  CERTS_RIGHT_RAIL_WIDTH,
  certsHeroMetricCardSx,
  certsHeroMetricCardHighlightSx,
  certsCardHeaderSx,
  certsSectHeadSx,
  certsSectIconSx,
  certsSettingRowSx,
  certsMutedSettingRowSx,
  certsRecItemSx,
  certsCardShellSx,
  certsCardBodySx,
} from "./certsLayout";
import {
  SSLOverviewIconChip,
  SSL_OVERVIEW_ICON_SX,
  sslOverviewLabelSx,
} from "./components/SSLOverviewMetricCard";
import { GreenSwitch } from "../../../common/Greens";
import { GhostButton, ghostButtonSmSx, PrimaryButton } from "../../../common/Button";
import useSSLConfig from "../../../../hooks/user/useSSLConfig";
import useGlobalSite from "../../../../hooks/user/useGlobalSite";
import useAuth from "../../../../hooks/useAuth";
import { useNotification } from "../../../../contexts/NotificationContext";
import {
  SslType, TlsVersion, TlsProfile, CertificateType, UserType,
  TLS12_CIPHERS, TLS13_CIPHERS, TLS_MIN_CIPHER_COUNT_TLS12,
} from "../../../../utils/constants";
import SSLConfigurationUpload from "../sites/SSLConfigurationUpload";
import SSLConfigurationGenerateOrigin from "../sites/SSLConfigurationGenerateOrigin";
import SslModeCompareModal from "./SslModeCompareModal";
import {
  ShieldCheck, FileLock2, X as LucideX, Check as LucideCheck,
  ArrowRight, RotateCw, Cloud, Building2, Upload as LucideUpload,
  Lock as LucideLock, Info as LucideInfo, ArrowRightCircle, Copy as LucideCopy,
  Radar as LucideRadar, CheckCircle2, ShieldPlus,
} from "lucide-react";
import "./SSLTLSPage.drawer.css";

const INDIGO       = colors.primary[300];
const INDIGO_INK   = colors.primary[500];
const INDIGO_SOFT  = "rgba(99,113,247,0.10)";
const INDIGO_MID   = "rgba(99,113,247,0.18)";
const INDIGO_BORDER= "rgba(99,113,247,0.30)";
const GREEN_DARK   = colors.green[300];
const RED_DARK     = colors.red[200];
const FAINT_GREY   = CERTS_FAINT_GREY;
const INFO_BANNER_BG = INDIGO_SOFT;

/** Vertical rhythm — Layerd-UI ssl-tls/configuration.html */
const PAGE_SECTION_SPACING = CERTS_PAGE_SECTION_SPACING;
const CARD_GRID_GAP = CERTS_CARD_GRID_GAP;
const GRID_GAP = CERTS_GRID_GAP;
const INNER_STACK_SPACING = CERTS_INNER_STACK_SPACING;
const SECTION_HEADER_MB = CERTS_SECTION_HEADER_MB;
const CARD_PAD = CERTS_CARD_PAD;
const MODE_INFO_BANNER_MT = 2;
const RIGHT_RAIL_WIDTH = CERTS_RIGHT_RAIL_WIDTH;

// ─── HELPERS ────────────────────────────────────────────────────────
const daysUntil = (d) => {
  if (!d) return null;
  const ms = new Date(d).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (24 * 3600 * 1000)));
};

const SSL_MODES = [
  { id: SslType.OFF, key: "insecure", title: "InSecure", tone: "danger",
    desc: "No encryption is applied. Traffic flows in plaintext between browser and origin.",
    level: 1, label: "Least secured",
    bestFor: "testing only — never use in production." },
  { id: SslType.FLEXIBLE, key: "adaptive", title: "Adaptive", tone: "warn",
    desc: "Encrypts traffic between the browser and Layerd. Layerd-to-origin remains in plaintext.",
    level: 2, label: "Medium",
    bestFor: "origins that cannot yet support HTTPS." },
  { id: SslType.FULL, key: "adaptive-e2e", title: "Adaptive E2E", tone: "ok",
    desc: "End-to-end encryption using a self-signed certificate on the origin. Authenticity not validated.",
    level: 3, label: "Secured",
    bestFor: "most sites that need full encryption without strict origin validation." },
  { id: SslType.FULL_STRICT, key: "advanced-e2e", title: "Advanced E2E", tone: "best",
    desc: "End-to-end encryption with a trusted CA or Layerd Origin CA certificate validated at the origin.",
    level: 4, label: "Most secured",
    bestFor: "production sites requiring strict certificate validation at the origin." },
];

/** Section title row — title + icon left, description right (Figma ssl-tls layout). */
function SectionHeader({ icon, title, description }) {
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems={{ xs: "flex-start", md: "center" }}
      justifyContent="space-between"
      gap={{ xs: 1.5, md: 2.5 }}
      sx={{ mb: SECTION_HEADER_MB }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        {icon}
        <Typography component="h2" sx={certsSectionTitleSx}>
          {title}
        </Typography>
      </Stack>
      {description && (
        <Typography
          sx={{
            ...certsBodySmallSx,
            color: colors.neutral[700],
            maxWidth: 480,
            textAlign: { xs: "left", md: "right" },
          }}
        >
          {description}
        </Typography>
      )}
    </Stack>
  );
}

const MODE_TONE_STYLES = {
  danger: { bg: "rgba(251,55,72,0.10)", fg: RED_DARK },
  warn:   { bg: "rgba(255,219,67,0.18)", fg: "#7a5b10" },
  ok:     { bg: "rgba(31,193,107,0.10)", fg: GREEN_DARK },
  best:   { bg: INDIGO_SOFT, fg: INDIGO_INK },
};

// ─── Pill component ────────────────────────────────────────────────
function Pill({ tone = "default", children, sx }) {
  const tones = {
    default: { bg: colors.neutral[100], fg: colors.neutral[700], bd: colors.neutral[200] },
    off:     { bg: colors.neutral[200], fg: colors.neutral[500], bd: colors.neutral[200] },
    ok:      { bg: "rgba(31,193,107,0.10)", fg: GREEN_DARK,         bd: "rgba(31,193,107,0.30)" },
    warn:    { bg: "rgba(255,219,67,0.18)", fg: "#7a5b10",          bd: "rgba(223,180,0,0.40)" },
    danger:  { bg: "rgba(251,55,72,0.10)",  fg: RED_DARK,           bd: "rgba(251,55,72,0.30)" },
    info:    { bg: INDIGO_SOFT,             fg: INDIGO_INK,         bd: INDIGO_BORDER },
  };
  const t = tones[tone] || tones.default;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5,
      px: 1.1, height: 22, borderRadius: 999,
      ...certsPillTextSx, whiteSpace: "nowrap",
      backgroundColor: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
      ...sx,
    }}>
      {children}
    </Box>
  );
}

const dotSx = { width: 6, height: 6, borderRadius: "50%", backgroundColor: "currentColor", display: "inline-block" };

// ─── Rating ring gauge ──────────────────────────────────────────────
function RatingRing({ grade, score }) {
  // grade letter → ring stroke color
  const palette = (() => {
    const g = (grade || "").toUpperCase();
    if (g.startsWith("A")) return { stroke: GREEN_DARK, text: GREEN_DARK, fillPct: 88 };
    if (g.startsWith("B")) return { stroke: "#F2B429",  text: "#7a5b10",  fillPct: 65 };
    if (g.startsWith("C")) return { stroke: "#F2B429",  text: "#7a5b10",  fillPct: 50 };
    if (g.startsWith("D")) return { stroke: RED_DARK,   text: RED_DARK,   fillPct: 30 };
    if (g.startsWith("F")) return { stroke: RED_DARK,   text: RED_DARK,   fillPct: 15 };
    return { stroke: colors.neutral[400], text: colors.neutral[600], fillPct: 0 };
  })();
  const pct = typeof score === "number" ? Math.max(0, Math.min(100, score)) : palette.fillPct;
  const circ = 2 * Math.PI * 32;
  return (
    <Box sx={{ position: "relative", width: 74, height: 74, flexShrink: 0 }}>
      <svg width={74} height={74} viewBox="0 0 74 74" style={{ transform: "rotate(-90deg)" }}>
        <circle cx={37} cy={37} r={32} fill="none" stroke="#F4F4F4" strokeWidth={6} />
        <circle cx={37} cy={37} r={32} fill="none"
          stroke={palette.stroke} strokeWidth={6} strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} />
      </svg>
      <Box sx={{
        position: "absolute", inset: 0, display: "flex",
        alignItems: "center", justifyContent: "center",
        ...certsGradeDisplaySx,
        color: palette.text,
      }}>{grade || "—"}</Box>
    </Box>
  );
}

// ─── HERO CARDS ─────────────────────────────────────────────────────
function HeroCard({ children, highlight, sx }) {
  return (
    <Box
      sx={{
        ...certsHeroMetricCardSx,
        ...(highlight ? certsHeroMetricCardHighlightSx : {}),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function HeroStrip({ sslConfig, sslRating, sslRatingLoading, sslRatingProgress }) {
  const certExpiresOn = sslConfig?.certs?.validTo || sslConfig?.certs?.expiresOn || sslConfig?.certs?.expires_on;
  const certIssuedOn  = sslConfig?.certs?.validFrom || sslConfig?.certs?.issuedOn  || sslConfig?.certs?.issued_on;
  const dleft = daysUntil(certExpiresOn);
  const filledPct = certIssuedOn && certExpiresOn
    ? Math.max(0, Math.min(100, ((new Date() - new Date(certIssuedOn)) / (new Date(certExpiresOn) - new Date(certIssuedOn))) * 100))
    : null;
  const remainingPct = filledPct === null ? null : Math.max(0, 100 - filledPct);

  const currentMode = SSL_MODES.find((m) => m.id === sslConfig?.ssl_type) || SSL_MODES[0];

  const metricOverlineIcon = (IconComponent) => (
    <SSLOverviewIconChip variant="primary">
      <IconComponent sx={SSL_OVERVIEW_ICON_SX} />
    </SSLOverviewIconChip>
  );
  const overlineRow = (IconComponent, label) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
      {metricOverlineIcon(IconComponent)}
      <Typography component="span" sx={sslOverviewLabelSx}>{label}</Typography>
    </Stack>
  );
  const value = { ...certsStatSx };
  const sub = { ...certsBodySecondarySx, mt: 0.5, fontVariantNumeric: "tabular-nums" };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1.2fr 1fr 1fr 1fr" },
        gap: CARD_GRID_GAP,
      }}
    >
      <HeroCard highlight sx={{ justifyContent: "flex-start" }}>
        {overlineRow(StarIcon, "Overall rating")}
        <Stack direction="row" alignItems="center" spacing={1.75} sx={{ width: "100%" }}>
          {sslRatingLoading ? (
            <Box sx={{ position: "relative", width: 74, height: 74, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <CircularProgress size={56} sx={{ color: INDIGO }} />
            </Box>
          ) : <RatingRing grade={sslRating?.grade} score={sslRating?.score} />}
          <Box sx={{ minWidth: 0, flex: 1 }}>
          {sslRatingLoading ? (
            <Typography sx={{ ...certsBodySmallSx, color: colors.neutral[700] }}>
              Scanning… {typeof sslRatingProgress === "number" ? `${Math.round(sslRatingProgress)}%` : ""}
            </Typography>
          ) : sslRating?.grade ? (
            <>
              <Typography sx={{ ...certsBodySx, fontWeight: 700 }}>{ratingHeadline(sslRating.grade)}</Typography>
              <Typography sx={{ ...certsBodySecondarySx, mt: 0.25 }}>
                {sslRating.created_at
                  ? `Last scan ${formatDate(sslRating.created_at)}`
                  : sslRating.updated_at
                  ? `Last scan ${formatDate(sslRating.updated_at)}`
                  : "Latest scan"}
              </Typography>
            </>
          ) : sslRating?.status === "error" ? (
            <>
              <Typography sx={{ ...certsBodySmallSx, fontWeight: 600, color: RED_DARK }}>Scan failed</Typography>
              <Typography sx={{ ...certsBodySecondarySx, mt: 0.25 }}>
                {sslRating.message || sslRating.statusMessage || "Try Refresh to retry."}
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ ...certsBodySmallSx, fontWeight: 600 }}>Not yet scanned</Typography>
              <Typography sx={{ ...certsBodySecondarySx, mt: 0.25 }}>
                Rating will appear after the next automatic scan.
              </Typography>
            </>
          )}
          </Box>
        </Stack>
      </HeroCard>

      <HeroCard>
        {overlineRow(CalendarIcon, "Certificate expires")}
        <Typography sx={value}>{dleft != null ? `in ${dleft} day${dleft === 1 ? "" : "s"}` : "—"}</Typography>
        {filledPct != null && (
          <Box sx={{ mt: 1.25 }}>
            <Box sx={{ height: 6, borderRadius: 3, backgroundColor: colors.neutral[100], position: "relative", overflow: "hidden" }}>
              <Box sx={{
                position: "absolute", left: 0, top: 0, bottom: 0,
                width: `${remainingPct}%`,
                background: `linear-gradient(90deg, ${dleft < 14 ? RED_DARK : dleft < 30 ? "#F2B429" : GREEN_DARK}, ${dleft < 14 ? "#FBA0A8" : dleft < 30 ? "#FFE89A" : "#7DD89E"})`,
                borderRadius: 3,
              }} />
            </Box>
            <Stack direction="row" justifyContent="space-between" sx={{ ...certsBodySecondarySx, color: colors.neutral[600], mt: 0.5 }}>
              <span>Issued · {certIssuedOn ? new Date(certIssuedOn).toLocaleDateString() : "—"}</span>
              <span>Expires · {certExpiresOn ? new Date(certExpiresOn).toLocaleDateString() : "—"}</span>
            </Stack>
          </Box>
        )}
      </HeroCard>

      <HeroCard>
        {overlineRow(ShieldOutlinedIcon, "Encryption mode")}
        <Typography sx={value}>{currentMode.title}</Typography>
        <Typography sx={sub}>{currentMode.label}</Typography>
      </HeroCard>

      <HeroCard>
        {overlineRow(ActivityIcon, "HTTPS traffic · 24h")}
        <Typography sx={{ ...value, color: colors.neutral[500] }}>—</Typography>
        <Typography sx={sub}>
          Per-protocol traffic accounting is not yet exposed on this dashboard.
        </Typography>
      </HeroCard>
    </Box>
  );
}

// Server persists cipher_list / ciphersuites as colon-joined strings.
// The configSslSetting handler expects an array (it joins back on write).
function parseCipherString(s) {
  if (Array.isArray(s)) return s;
  if (typeof s !== "string" || !s) return [];
  return s.split(":").map((t) => t.trim()).filter(Boolean);
}

function ratingHeadline(g) {
  const u = (g || "").toUpperCase();
  if (u === "A+") return "Excellent · best practice";
  if (u === "A") return "Excellent";
  if (u === "B") return "Good · room to improve";
  if (u === "C") return "Fair · improvements recommended";
  if (u === "D") return "Poor · action needed";
  if (u === "F") return "Failing · urgent action";
  return "Unknown";
}

// ─── ENCRYPTION LADDER ─────────────────────────────────────────────
function ModeLadder({ selectedSslType, appliedSslType, onSelect, disabled }) {
  const modeChanged = appliedSslType != null && selectedSslType !== appliedSslType;
  return (
    <Box
      id="ssl-mode-ladder"
      sx={{
        pt: 1.5,
        overflow: "visible",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
        gap: CARD_GRID_GAP,
      }}
    >
      {SSL_MODES.map((mode) => {
        const selected = mode.id === selectedSslType;
        const isApplied = mode.id === appliedSslType;
        const badgeLabel = modeChanged && selected
          ? "Selected"
          : isApplied
            ? "Current"
            : null;
        const tone = MODE_TONE_STYLES[mode.tone];
        const Icon = mode.tone === "danger" ? GppBadIcon
                   : mode.tone === "warn"   ? SyncIcon
                   : mode.tone === "ok"     ? LockIcon
                                            : GppGoodIcon;
        return (
          <Box
            key={mode.id}
            onClick={() => !disabled && onSelect(mode.id)}
            sx={{
              position: "relative",
              p: "18px 18px 16px",
              backgroundColor: "#fff",
              backgroundImage: selected
                ? "linear-gradient(180deg, rgba(99,113,247,0.06) 0%, rgba(255,255,255,0) 100%)"
                : "none",
              border: `1px solid ${selected ? INDIGO : colors.neutral[200]}`,
              borderRadius: "12px",
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "border-color 160ms, box-shadow 160ms",
              minHeight: 188,
              display: "flex",
              flexDirection: "column",
              boxShadow: selected ? `0 0 0 1px ${INDIGO_SOFT}` : "0 1px 2px rgba(17, 17, 17, 0.04)",
              "&:hover": disabled
                ? {}
                : {
                    borderColor: selected ? INDIGO : colors.neutral[400],
                    boxShadow: "0 2px 8px rgba(17, 17, 17, 0.06)",
                  },
              opacity: disabled ? 0.7 : 1,
            }}
          >
            {badgeLabel && (
              <Box sx={{
                position: "absolute",
                top: -10,
                left: 18,
                transform: "none",
                backgroundColor: INDIGO,
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                px: 1.25,
                py: 0.35,
                borderRadius: "4px",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
              >
                {badgeLabel}
              </Box>
            )}
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2.5 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: "8px",
                backgroundColor: tone.bg,
                color: tone.fg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              >
                <Icon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                border: `1.5px solid ${selected ? INDIGO : colors.neutral[400]}`,
                backgroundColor: selected ? INDIGO : "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              >
                {selected && (
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#fff" }} />
                )}
              </Box>
            </Stack>
            <Typography sx={{ ...certsBodySx, fontWeight: 700, mb: 0.75 }}>
              {mode.title}
            </Typography>
            <Typography sx={{ ...certsBodySecondarySx, flex: 1, mb: 2 }}>
              {mode.desc}
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <LevelBars level={mode.level} />
              <Typography component="span" sx={{ ...certsBodySecondarySx, color: tone.fg, fontWeight: 600 }}>
                {mode.label}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

function LevelBars({ level }) {
  const heights = [4, 7, 10, 14];
  const palettes = [null, RED_DARK, "#F2B429", GREEN_DARK, INDIGO];
  return (
    <Stack direction="row" spacing={0.25} alignItems="flex-end" sx={{ height: 14 }}>
      {heights.map((h, i) => (
        <Box key={i} sx={{
          width: 3, height: h, borderRadius: "1px",
          backgroundColor: i < level ? palettes[level] : colors.neutral[200],
        }} />
      ))}
    </Stack>
  );
}

// ─── CARD shell ─────────────────────────────────────────────────────
function Card({ icon, title, action, children, sx }) {
  return (
    <Box sx={{ ...certsCardShellSx, ...sx }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={certsCardHeaderSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {icon}
          <Typography sx={certsCardTitleSx}>
            {title}
          </Typography>
        </Stack>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

function truncateSerial(serial) {
  if (!serial) return null;
  const s = String(serial);
  if (s.length <= 14) return s;
  return `${s.slice(0, 8)}…${s.slice(-2)}`;
}

const CERT_GRID_COLS = 4;

function CertGridCell({ index, total, children }) {
  const col4 = index % CERT_GRID_COLS;
  const row4 = Math.floor(index / CERT_GRID_COLS);
  const lastRow4 = Math.floor((total - 1) / CERT_GRID_COLS);
  const col2 = index % 2;
  const row2 = Math.floor(index / 2);
  const lastRow2 = Math.floor((total - 1) / 2);
  return (
    <Box sx={{
      p: "14px 18px",
      minWidth: 0,
      borderRight: {
        xs: col2 === 0 ? `1px solid ${colors.neutral[200]}` : 0,
        md: col4 < CERT_GRID_COLS - 1 ? `1px solid ${colors.neutral[200]}` : 0,
      },
      borderBottom: {
        xs: row2 < lastRow2 ? `1px solid ${colors.neutral[200]}` : 0,
        md: row4 < lastRow4 ? `1px solid ${colors.neutral[200]}` : 0,
      },
    }}>
      {children}
    </Box>
  );
}

function CertGridLabel({ children }) {
  return (
    <Typography sx={{ ...certsOverlineSx, letterSpacing: "0.05em", mb: 0.75, display: "block" }}>
      {children}
    </Typography>
  );
}

function CertGridValue({ children, mono, title }) {
  return (
    <Typography component="div" title={title} sx={mono ? certsGridMonoValueSx : certsGridValueSx}>
      {children}
    </Typography>
  );
}

const CHAIN_ROLE_META = {
  root: { sub: "Root CA", Icon: AccountBalanceOutlinedIcon },
  intermediate: { sub: "Intermediate", Icon: AccountTreeOutlinedIcon },
  leaf: { sub: "Leaf · current", Icon: PublicIcon },
};

function ChainLinkBar() {
  return (
    <Box sx={{
      flex: 1,
      height: 2,
      minWidth: 20,
      alignSelf: "center",
      mx: 1.5,
      background: `repeating-linear-gradient(to right, ${colors.neutral[200]} 0 4px, transparent 4px 8px)`,
    }} />
  );
}

function CertificateChain({ nodes }) {
  if (!nodes?.length) return null;
  return (
    <Box sx={{ backgroundColor: FAINT_GREY, borderTop: `1px solid ${colors.neutral[200]}` }}>
      <Box sx={{ px: 2.5, pt: 1.75, pb: 0.5 }}>
        <CertGridLabel>Certificate chain</CertGridLabel>
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ px: 2.5, pb: 2, pt: 0.75, overflowX: "auto" }}
      >
        {nodes.map((node, idx) => {
          const meta = CHAIN_ROLE_META[node.role] || CHAIN_ROLE_META.leaf;
          const Icon = meta.Icon;
          const isLeaf = node.role === "leaf";
          return (
            <React.Fragment key={`${node.cn}-${idx}`}>
              {idx > 0 && <ChainLinkBar />}
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexShrink: 0 }}>
                <Box sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  backgroundColor: isLeaf ? INDIGO_SOFT : colors.neutral[100],
                  color: isLeaf ? INDIGO : colors.neutral[700],
                  border: `1px solid ${isLeaf ? INDIGO_BORDER : colors.neutral[200]}`,
                }}>
                  <Icon sx={{ fontSize: 17 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ ...certsGridValueSx, lineHeight: "18px" }}>
                    {node.cn}
                  </Typography>
                  <Typography sx={{ ...certsBodySecondarySx, mt: 0.25, lineHeight: "15px" }}>
                    {meta.sub}
                  </Typography>
                </Box>
              </Stack>
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
}

// ─── ACTIVE CERTIFICATE ────────────────────────────────────────────
// Every field below is sourced from the real /config/ssl/:siteUid PEM
// parse on the CP — see helpers/ssl.js::parseFullCertDetails. Fields
// that aren't present on a given cert are simply hidden (no "—"
// placeholders, no fabricated fallbacks).
function ActiveCertificate({ sslConfig }) {
  const cert = sslConfig?.certs;
  if (!cert || !cert.host?.length) {
    return (
      <Card icon={<VerifiedUserIcon sx={{ fontSize: 16, color: INDIGO }} />} title="Active certificate">
        <Box sx={{ p: 4, textAlign: "center", color: colors.neutral[600], ...certsBodySmallSx }}>
          No certificate installed for this site yet. Use the Install card on the right to add one.
        </Box>
      </Card>
    );
  }
  const expired = cert.validTo ? new Date(cert.validTo) < new Date() : false;
  const statusPill = expired
    ? <Pill tone="danger"><Box sx={dotSx} />Expired</Pill>
    : <Pill tone="ok"><Box sx={dotSx} />Active</Pill>;
  const typeLabel = cert.type === CertificateType.SENSE_GUARD ? "Universal"
                  : cert.type === CertificateType.CUSTOM      ? "Custom"
                  : cert.type === CertificateType.LAYERD_MANAGED ? "Layerd managed"
                  : "—";

  const hostLabel = Array.isArray(cert.host) ? cert.host.join(", ") : cert.host;
  const expiresOn = cert.validTo ? formatDate(cert.validTo) : null;
  const serialDisplay = truncateSerial(cert.serial);
  const chainNodes = Array.isArray(cert.chain) && cert.chain.length > 0
    ? [...cert.chain].reverse()
    : [];

  const gridCells = [
    { label: "Host", content: <CertGridValue mono title={hostLabel}>{hostLabel}</CertGridValue> },
    { label: "Status", content: statusPill },
    { label: "Type", content: <CertGridValue>{typeLabel}</CertGridValue> },
    { label: "Expires on", content: <CertGridValue>{expiresOn || "—"}</CertGridValue> },
    { label: "Issuer", content: <CertGridValue>{cert.issuer || "—"}</CertGridValue> },
    { label: "Key algorithm", content: <CertGridValue mono>{cert.key_algorithm || "—"}</CertGridValue> },
    { label: "Signature", content: <CertGridValue mono>{cert.signature || "—"}</CertGridValue> },
    {
      label: "Serial",
      content: (
        <CertGridValue mono title={cert.serial || undefined}>
          {serialDisplay || "—"}
        </CertGridValue>
      ),
    },
  ];

  return (
    <Card
      icon={<VerifiedUserIcon sx={{ fontSize: 16, color: INDIGO }} />}
      title="Active certificate"
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        }}
      >
        {gridCells.map((cell, idx) => (
          <CertGridCell key={cell.label} index={idx} total={gridCells.length}>
            <CertGridLabel>{cell.label}</CertGridLabel>
            {cell.content}
          </CertGridCell>
        ))}
      </Box>
      <CertificateChain nodes={chainNodes} />
    </Card>
  );
}

// ─── DRAFT CERTIFICATE ─────────────────────────────────────────────
function DraftCertificate({ sslConfig, siteUid, disabled }) {
  const draft = sslConfig?.cert_draft;
  const { provisionInstall, getSSLConfig } = useSSLConfig();
  const { showSuccess, showError } = useNotification();
  const [activating, setActivating] = React.useState(false);

  if (!draft?.provider_order_id) return null;

  const ready = draft.provider_status === "issued_ready";
  const statusPill = ready
    ? <Pill tone="warn"><Box sx={dotSx} />Draft</Pill>
    : <Pill tone="default"><Box sx={dotSx} />Draft requested</Pill>;

  const typeLabel = draft.cert_type === CertificateType.SENSE_GUARD ? "Universal"
    : draft.cert_type === CertificateType.CUSTOM ? "Custom"
    : draft.cert_type === CertificateType.LAYERD_MANAGED ? "Layerd managed"
    : "Layerd managed";

  const expiresOn = draft.valid_to ? formatDate(draft.valid_to) : null;
  const providerLabel = draft.provider === "zerossl" ? "ZeroSSL"
    : draft.provider === "acm" ? "ACM"
    : draft.provider === "digicert" ? "DigiCert"
    : draft.provider || "—";

  const onManualActivate = async () => {
    if (disabled || !ready) return;
    setActivating(true);
    try {
      const ins = await provisionInstall(siteUid, { force: true });
      if (ins.status === "success" && ins.data?.activated) {
        showSuccess("New certificate is now active on the edge");
        await getSSLConfig?.(siteUid, true);
      } else {
        const errMsg = typeof ins.data === "string"
          ? ins.data
          : ins.data?.message || ins.data?.error;
        showError(errMsg || (ins.status === "success"
          ? "Certificate is ready but still serving the previous cert"
          : "Activation failed"));
      }
    } finally {
      setActivating(false);
    }
  };

  const gridCells = [
    { label: "Status", content: statusPill },
    { label: "Type", content: <CertGridValue>{typeLabel}</CertGridValue> },
    { label: "Provider", content: <CertGridValue>{providerLabel}</CertGridValue> },
    { label: "Expires on", content: <CertGridValue>{expiresOn || "—"}</CertGridValue> },
  ];

  return (
    <Card
      icon={<HistoryIcon sx={{ fontSize: 16, color: INDIGO }} />}
      title="Draft certificate"
      action={ready ? (
        <PrimaryButton
          disabled={disabled || activating}
          onClick={onManualActivate}
          sx={{
            height: 30,
            px: 1.25,
            fontSize: 12,
            fontWeight: 500,
            minWidth: "auto",
          }}
        >
          {activating ? "Activating…" : "Activate now"}
        </PrimaryButton>
      ) : null}
    >
      <Typography sx={{ ...certsBodySecondarySx, px: 2, pt: 0, pb: 1.5 }}>
        {ready
          ? "Ready to activate. Your current certificate keeps serving until you click Activate now, or until it expires within one hour — then this draft is applied automatically on the hourly check."
          : "Provisioning in progress. Your active certificate keeps serving until this order completes."}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        }}
      >
        {gridCells.map((cell, idx) => (
          <CertGridCell key={cell.label} index={idx} total={gridCells.length}>
            <CertGridLabel>{cell.label}</CertGridLabel>
            {cell.content}
          </CertGridCell>
        ))}
      </Box>
    </Card>
  );
}

// ─── SECTIONED CARD (HSTS / Protocol etc.) ─────────────────────────
function SectionedCard({ id, icon, title, desc, warning, children }) {
  return (
    <Box id={id} sx={certsCardShellSx}>
      <Stack direction="row" alignItems="center" sx={certsSectHeadSx}>
        <Box sx={{
          ...certsSectIconSx,
          ...(warning && {
            backgroundColor: "rgba(255,219,67,0.18)",
            color: "#7a5b10",
            border: "1px solid rgba(223,180,0,0.40)",
          }),
        }}>{icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...certsOverlineSx, color: colors.neutral[1000] }}>{title}</Typography>
          <Typography sx={{ ...certsBodySecondarySx, fontSize: 11.5, lineHeight: "25px", mt: 0.125 }}>{desc}</Typography>
        </Box>
        {warning && (
          <Pill tone="warn" sx={{ flexShrink: 0 }}>
            <WarningAmberIcon sx={{ fontSize: 11 }} />Can lock users out
          </Pill>
        )}
      </Stack>
      <Box>{children}</Box>
    </Box>
  );
}

function SettingRow({ icon, name, desc, control, last, muted = false }) {
  const mutedIconColor = muted ? colors.neutral[500] : colors.neutral[600];

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        ...certsSettingRowSx,
        borderBottom: last ? 0 : certsSettingRowSx.borderBottom,
        ...(muted ? certsMutedSettingRowSx : null),
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.375 }}>
          {React.isValidElement(icon) && React.cloneElement(icon, {
            sx: { fontSize: 14, width: 14, height: 14, color: mutedIconColor, ...icon.props?.sx },
          })}
          <Typography sx={{
            ...certsBodySmallSx,
            fontSize: 13.5,
            fontWeight: 500,
            lineHeight: "18px",
            ...(muted ? { color: colors.neutral[500] } : null),
          }}
          >
            {name}
          </Typography>
        </Stack>
        <Typography sx={{
          ...certsBodySecondarySx,
          lineHeight: "17px",
          ...(muted ? { color: colors.neutral[500] } : null),
        }}
        >
          {desc}
        </Typography>
      </Box>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexShrink: 0 }}>
        {control}
      </Stack>
    </Stack>
  );
}

// ─── INSTALL CERT DRAWER ────────────────────────────────────────────
const fieldLabelSx = { ...certsFieldLabelSx, color: colors.neutral[700], fontWeight: 500, mb: 0.75 };
const inputSx = {
  ...certsBodySmallSx,
  "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: colors.neutral[100] },
};

const PROVIDER_GUIDE = {
  zerossl: {
    howItWorks: "Layerd orders a free 90-day DV certificate for your site wildcard, validates domain ownership via DNS, installs it at the edge, and renews it before expiry.",
    bullets: [
      "Served to visitors at the Layerd edge — no origin server changes required for the public cert.",
      "Domain control is proven with CNAME records at your DNS provider (DCV).",
      "After validation, the certificate is installed automatically across edge nodes.",
      "Renewal is handled by Layerd for standard Universal SSL setups.",
    ],
    next: {
      pick: "Click Continue, confirm the wildcard domain, then start provisioning and add the DNS records shown.",
      configure: "Add optional SAN hostnames if needed, then start provisioning to receive DNS validation records.",
      dcv: "Publish every CNAME record at your DNS host. Layerd re-checks about every 15 seconds until validation succeeds.",
    },
  },
  acm: {
    howItWorks: "Layerd requests a public certificate through AWS Certificate Manager, validates the domain via DNS, and deploys the issued cert to the edge for your site.",
    bullets: [
      "Uses ACM's public, exportable certificate workflow with DNS validation.",
      "You add the CNAME records ACM requires at your DNS provider.",
      "Layerd installs the issued certificate at the edge once validation completes.",
      "Counts against your organisation's managed-certificate quota when enabled.",
    ],
    next: {
      pick: "Click Continue to configure the request, then start provisioning when ready.",
      configure: "Confirm hostnames and start provisioning to receive ACM DNS validation records.",
      dcv: "Add the CNAME records to DNS. Validation may take from under a minute to several minutes.",
    },
  },
  digicert: {
    howItWorks: "Layerd provisions a DigiCert DV certificate for your domain, with DNS validation and installation at the edge after the order is approved.",
    bullets: [
      "Higher-trust DV issuance than auto-issued Universal SSL in many deployments.",
      "DNS validation records are provided after you start provisioning.",
      "Installed at the edge once DigiCert validates domain control.",
      "Subject to your organisation's DigiCert quota and admin enablement.",
    ],
    next: {
      pick: "Click Continue to configure hostnames, then start provisioning.",
      configure: "Review SANs if needed and start provisioning to receive validation records.",
      dcv: "Publish the DNS records at your provider and wait for DigiCert validation to complete.",
    },
  },
  custom: {
    howItWorks: "You upload a certificate and private key from any CA. Layerd deploys that pair to the edge for HTTPS termination — you manage renewal and validity.",
    bullets: [
      "Supports any public or private CA, wildcard or multi-host certs you already own.",
      "You are responsible for renewal before expiry and for keeping the key secure.",
      "No DNS validation step with Layerd — upload completes the install flow.",
      "Best when you must use an existing corporate or EV/OV certificate at the edge.",
    ],
    next: {
      pick: "Click Continue to open the upload form and paste or select your .crt and .key files.",
      configure: "Upload the certificate chain and private key, then save to deploy to the edge.",
      dcv: null,
    },
  },
  origin: {
    howItWorks: "Layerd generates an origin certificate signed by the Layerd Origin CA. Install it on your backend, then use Advanced E2E so Layerd validates the origin on every connection.",
    bullets: [
      "This cert encrypts traffic between Layerd and your origin — not what browsers see directly.",
      "Generate and download the cert/key, then install them on your web server or load balancer.",
      "Set encryption mode to Advanced E2E so Layerd requires a valid origin certificate.",
      "Origin CA certs are long-lived; rotate when you regenerate or change origins.",
    ],
    next: {
      pick: "Click Continue to generate an origin certificate for this site.",
      configure: "Generate the certificate, download it, and install it on your origin before enabling Advanced E2E.",
      dcv: null,
    },
  },
};

const providerGuideListSx = {
  m: 0,
  mt: 1,
  pl: 2.25,
  flex: 1,
  "& li": {
    fontSize: 12,
    lineHeight: "18px",
    color: colors.neutral[700],
    mb: 0.75,
    "&:last-child": { mb: 0 },
  },
};

/** LDCP-97 — selected issuer detail: API description + how it works / what to do. */
function SelectedProviderDetail({ provider, step = "pick" }) {
  if (!provider) return null;
  const hasDescription = Boolean(provider.description?.trim());
  const guide = PROVIDER_GUIDE[provider.id];
  const nextHint = guide?.next?.[step] ?? guide?.next?.pick;
  const fillPickStep = step === "pick";

  return (
    <Box
      sx={{
        mt: 2.5,
        mb: 2,
        p: 2.25,
        borderRadius: "10px",
        backgroundColor: INDIGO_SOFT,
        border: `1px solid ${INDIGO_BORDER}`,
        display: "flex",
        flexDirection: "column",
        minHeight: fillPickStep ? 280 : 0,
        flex: fillPickStep ? 1 : "none",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography sx={{ ...certsBodySx, fontWeight: 600, lineHeight: "21px" }}>
          {provider.label}
        </Typography>
        {provider.default && <Pill tone="info">Recommended</Pill>}
        {!provider.usable && <Pill tone="warn">Unavailable</Pill>}
      </Stack>

      {hasDescription && (
        <Typography sx={{ ...certsBodySecondarySx, lineHeight: "17px", mb: 1.25 }}>
          {provider.description}
        </Typography>
      )}

      {provider.remaining != null && provider.usable && (
        <Typography sx={{
          ...certsBodySecondarySx,
          color: colors.neutral[600],
          mb: 1.25,
          fontVariantNumeric: "tabular-nums",
        }}>
          {provider.used}/{provider.max_certs} used · {provider.remaining} remaining
        </Typography>
      )}

      {provider.reason && (
        <Typography sx={{ fontSize: 12, color: "#7a5b10", mb: 1.25, lineHeight: "17px" }}>
          {provider.reason}
        </Typography>
      )}

      {guide && (
        <>
          {guide.howItWorks && (
            <Typography sx={{ ...certsBodySmallSx, fontWeight: 500, lineHeight: "20px", mb: 0.75 }}>
              {guide.howItWorks}
            </Typography>
          )}
          {guide.bullets?.length > 0 && (
            <>
              <Typography sx={{ ...certsOverlineSx, fontSize: 10, letterSpacing: "0.05em", mb: 0.25 }}>
                How it works
              </Typography>
              <Box component="ul" sx={providerGuideListSx}>
                {guide.bullets.map((line) => (
                  <Box component="li" key={line}>{line}</Box>
                ))}
              </Box>
            </>
          )}
          {provider.usable && nextHint && (
            <Box sx={{
              mt: "auto",
              pt: 1.5,
              borderTop: `1px solid ${INDIGO_BORDER}`,
            }}>
              <Typography sx={{ ...certsOverlineSx, fontSize: 10, letterSpacing: "0.05em", mb: 0.5 }}>
                What to do next
              </Typography>
              <Typography sx={{ ...certsBodySmallSx, lineHeight: "20px", color: INDIGO_INK, fontWeight: 500 }}>
                {nextHint}
              </Typography>
            </Box>
          )}
        </>
      )}

      {!provider.usable && !guide && !provider.reason && (
        <Typography sx={{ ...certsBodySecondarySx, fontStyle: "italic" }}>
          Select an available provider to continue.
        </Typography>
      )}
    </Box>
  );
}

// Provisioning wizard — replaces the 3-tab Install drawer with a
// provider-driven flow backed by the real /config/ssl/:siteUid/provision*
// routes. Universal pre-selected on first open. Polls /provision/status
// every 15s while pending; auto-installs on issued.
// Provider-id ↔ design icon mapping. The data plane uses the canonical
// `zerossl` / `acm` / `digicert` / `custom` / `origin` ids; the design
// handoff visually rebrands them but keeps the IDs intact.
const PROVIDER_ICON_FOR = (id) => {
  switch (id) {
    case "zerossl":  return ShieldCheck;
    case "acm":      return Cloud;
    case "digicert": return Building2;
    case "custom":   return LucideUpload;
    case "origin":   return FileLock2;
    default:         return ShieldCheck;
  }
};

// Long-form "how it works" bullets (reused from PROVIDER_GUIDE so we
// don't duplicate copy — the new gradient detail panel renders them
// with the design's tick-bullet style).
const _providerGuide = (id) => PROVIDER_GUIDE[id] || null;

/* ---------- Step rail ---------- */
const RAIL_STEPS = [
  { n: 1, label: "Provider" },
  { n: 2, label: "Configure" },
  { n: 3, label: "Validate" },
  { n: 4, label: "Installed" },
];
function Rail({ step }) {
  return (
    <div className="ld-rail">
      {RAIL_STEPS.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className={`ld-rstep ${step === s.n ? "active" : ""} ${step > s.n ? "done" : ""}`}>
            <div className="num">{step > s.n ? <LucideCheck /> : s.n}</div>
            <div className="lbl">{s.label}</div>
          </div>
          {i < RAIL_STEPS.length - 1 && (
            <div className={`ld-rconn ${step > s.n ? "done" : ""}`}><div className="fill" /></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ---------- Step 1: provider grid + detail ---------- */
function StepProvider({ providers, providerId, setProviderId }) {
  const sel = providers?.find((p) => p.id === providerId);
  const selGuide = sel ? _providerGuide(sel.id) : null;
  return (
    <div className="ld-step-pane">
      <p className="ld-lead">
        Choose which provider should issue the certificate served at the edge. Layerd absorbs the cost for managed providers.
      </p>
      <div className="ld-prov-grid">
        {(providers || []).map((p) => {
          const Icon = PROVIDER_ICON_FOR(p.id);
          const isSel = providerId === p.id;
          const disabled = !p.usable;
          return (
            <button
              type="button"
              key={p.id}
              className={`ld-prov ${isSel ? "sel" : ""} ${disabled ? "disabled" : ""}`}
              onClick={() => !disabled && setProviderId(p.id)}
              disabled={disabled}
            >
              <span className="ld-radio" />
              <div className="ld-prov-top">
                <div className="ld-prov-ic"><Icon /></div>
                <div className="ld-prov-headline">
                  <span className="ld-prov-name">{p.label}</span>
                  {p.default && <span className="ld-pill rec">Recommended</span>}
                  {!p.enabled && <span className="ld-pill off">disabled</span>}
                </div>
              </div>
              {p.description && <div className="ld-prov-desc">{p.description}</div>}
              {p.remaining != null && p.usable && (
                <div className="ld-prov-meta">
                  {p.used}/{p.max_certs} used · {p.remaining} remaining
                </div>
              )}
              {p.reason && <div className="ld-prov-note">{p.reason}</div>}
            </button>
          );
        })}
      </div>

      {sel && sel.usable && (
        <div className="ld-detail" key={sel.id}>
          <div className="ld-detail-head">
            <span className="nm">{sel.label}</span>
            {sel.default && <span className="ld-pill rec">Recommended</span>}
          </div>
          {selGuide?.howItWorks && (
            <p className="ld-detail-summary">{selGuide.howItWorks}</p>
          )}
          {selGuide?.bullets?.length > 0 && (
            <>
              <div className="ld-overline" style={{ marginBottom: 10 }}>How it works</div>
              <ul className="ld-howto">
                {selGuide.bullets.map((b, i) => (
                  <li key={i}><span className="tick"><LucideCheck /></span>{b}</li>
                ))}
              </ul>
            </>
          )}
          {selGuide?.next?.pick && (
            <div className="ld-detail-next">
              <ArrowRightCircle />
              <span>{selGuide.next.pick}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Step 2: configure ---------- */
function StepConfigure({ provider, domain, sans, setSans, goProvider }) {
  if (!provider) return null;
  const Icon = PROVIDER_ICON_FOR(provider.id);
  return (
    <div className="ld-step-pane">
      <div className="ld-chosen">
        <div className="ic"><Icon /></div>
        <div>
          <div className="nm">{provider.label}</div>
          {provider.description && <div className="ds">{provider.description}</div>}
        </div>
        <button type="button" className="chg" onClick={goProvider}>Change</button>
      </div>

      <div className="ld-field">
        <label>Domain</label>
        <div className="ld-input-wrap locked">
          <input
            className="ld-input locked"
            value={domain}
            readOnly
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
          />
          <span className="ld-input-lock" aria-hidden="true"><LucideLock /></span>
        </div>
        <div className="ld-hint">Provisioned for the wildcard of this site.</div>
      </div>

      <div className="ld-field">
        <label>Subject Alternative Names <span className="opt">(optional)</span></label>
        <input
          className="ld-input"
          placeholder="api.example.com, cdn.example.com"
          value={sans}
          onChange={(e) => setSans(e.target.value)}
        />
        <div className="ld-hint">Comma-separated additional hostnames the cert should cover.</div>
      </div>

      <div className="ld-callout">
        <LucideInfo />
        <span>
          After you click <b>Start provisioning</b>, {provider.label} will return DNS CNAME records you need to publish on
          your domain. Layerd polls the provider every 15s and installs the cert as soon as validation completes.
        </span>
      </div>
    </div>
  );
}

/* ---------- Step 3: provisioning + validate ---------- */
function provTasksForProvider(providerId) {
  const csr = providerId === "acm"
    ? "Submitting request to AWS ACM"
    : providerId === "digicert"
      ? "Submitting CSR to DigiCert"
      : "Submitting CSR to ZeroSSL";
  return [
    "Reserving certificate order",
    csr,
    "Generating DNS challenge",
    "Preparing edge install",
  ];
}

function StepProvisioningSpinner({ provider, orderInfo }) {
  // Drive the checklist off real backend state where we can.
  // 0 = before submit returns, 1 = submit done (orderInfo present), 2 = dcv records arrived
  const tasks = provTasksForProvider(provider?.id);
  let active = 0;
  if (orderInfo?.order_id) active = 1;
  if ((orderInfo?.dcv?.records || []).length > 0) active = 2;
  // Show a busy "preparing edge install" final step once we have records — the next 15s poll completes it.
  return (
    <div className="ld-step-pane ld-provisioning">
      <div className="ld-spinner">
        <svg viewBox="0 0 100 100">
          <circle className="track" cx="50" cy="50" r="44" />
          <circle className="arc" cx="50" cy="50" r="44" />
        </svg>
        <div className="glyph"><ShieldPlus /></div>
      </div>
      <h2>Ordering your certificate</h2>
      <div className="ld-pmsg">{tasks[active]}…</div>
      <div className="ld-prov-steps">
        {tasks.map((t, i) => (
          <div key={i} className={`ld-pstep ${i < active ? "done" : i === active ? "busy" : ""}`}>
            <span className="pc">
              {i < active ? <LucideCheck /> : i === active ? <span className="mini-spin" /> : null}
            </span>
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}

function StepValidate({ provider, orderInfo, verified, countdown, copyState, onCopy }) {
  // Cached provider_dcv may still hold one row per SAN with identical
  // name/value (legacy multi-SAN orders). Collapse for display.
  const rawRecords = orderInfo?.dcv?.records || [];
  const seenKeys = new Set();
  const records = rawRecords.filter((r) => {
    if (!r || r.name == null || r.value == null) return false;
    const key = `${String(r.type || "CNAME").toUpperCase()}|${r.name}|${r.value}`;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });
  const recheckPct = (15 - Math.max(0, Math.min(15, countdown))) / 15;
  return (
    <div className="ld-step-pane">
      <div className={`ld-status-banner ${verified ? "ok" : "pending"}`}>
        {verified ? (
          <>
            <CheckCircle2 />
            <span className="grow">Domain validated</span>
          </>
        ) : (
          <>
            <span className="live">
              <span className="ping"><span className="halo" /><span className="core" /></span>
              Pending validation
            </span>
            <span className="grow" />
            <span className="ld-recheck">
              <span className="ring">
                <svg viewBox="0 0 16 16" width="16" height="16">
                  <circle className="t" cx="8" cy="8" r="6.5" />
                  <circle
                    className="f"
                    cx="8" cy="8" r="6.5"
                    strokeDasharray="40.8"
                    strokeDashoffset={40.8 * (1 - recheckPct)}
                  />
                </svg>
              </span>
              Re-checking in {countdown}s
            </span>
          </>
        )}
      </div>

      <p className="ld-dns-intro">
        {verified ? (
          <>All challenge records resolved. The certificate is being installed across edge nodes — this finishes in a few seconds.</>
        ) : (
          <>
            Add the following CNAME record{records.length === 1 ? "" : "s"} at your DNS provider. Layerd re-checks every{" "}
            <b>15s</b> and installs the certificate as soon as validation succeeds — this can take from 30s up to several
            minutes.
          </>
        )}
      </p>

      {records.length === 0 ? (
        <div className="ld-records-empty">
          Waiting for {provider?.label || "the provider"} to publish validation records…
        </div>
      ) : (
        records.map((r, i) => (
          <div key={i} className={`ld-rec ${verified ? "verified" : ""}`}>
            <div className="ld-rec-head">
              <span className="tag">{(r.type || "CNAME")} record</span>
              <span className="type">{r.type || "CNAME"}</span>
              <span className="grow" />
              {verified ? (
                <span className="ld-rec-state ok"><LucideCheck />Verified</span>
              ) : (
                <span className="ld-rec-state checking"><span className="mini-spin" />Checking</span>
              )}
            </div>
            <div className="ld-rec-row">
              <span className="k">Name</span>
              <span className="v">{r.name}</span>
              <button
                type="button"
                className={`ld-copy-btn ${copyState === `n${i}` ? "copied" : ""}`}
                onClick={() => onCopy(r.name, `n${i}`)}
                aria-label="Copy name"
              >
                {copyState === `n${i}` ? <LucideCheck /> : <LucideCopy />}
              </button>
            </div>
            <div className="ld-rec-row">
              <span className="k">Value</span>
              <span className="v">{r.value}</span>
              <button
                type="button"
                className={`ld-copy-btn ${copyState === `v${i}` ? "copied" : ""}`}
                onClick={() => onCopy(r.value, `v${i}`)}
                aria-label="Copy value"
              >
                {copyState === `v${i}` ? <LucideCheck /> : <LucideCopy />}
              </button>
            </div>
          </div>
        ))
      )}

      {!verified && records.length > 0 && (
        <div className="ld-poll-note">
          <LucideRadar />
          <span>Auto-polling</span>
          <span className="bar"><span className="fill" style={{ width: `${recheckPct * 100}%` }} /></span>
        </div>
      )}
    </div>
  );
}

/* ---------- Step 4: success ---------- */
function StepSuccess({ domain, sans, provider }) {
  const today = new Date();
  const expiry = new Date(today);
  expiry.setDate(expiry.getDate() + 90);
  const fmt = (d) => d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  const sanCount = sans.trim() ? sans.split(",").map((s) => s.trim()).filter(Boolean).length : 0;
  const coverage = domain.startsWith("*.")
    ? (sanCount > 0 ? `Wildcard + ${sanCount} SAN${sanCount > 1 ? "s" : ""}` : "Wildcard")
    : (sanCount > 0 ? `Apex + ${sanCount} SAN${sanCount > 1 ? "s" : ""}` : "Apex");
  const sub = provider?.id === "acm"
    ? `${provider.label} · DV · via AWS ACM`
    : provider?.id === "digicert"
      ? `${provider.label} · DV · via DigiCert`
      : `${provider?.label || "Universal SSL"} · DV · via ZeroSSL`;
  return (
    <div className="ld-step-pane ld-success">
      <div className="ld-success-badge">
        <svg className="ring" viewBox="0 0 94 94"><circle cx="47" cy="47" r="46" /></svg>
        <span className="check"><LucideCheck /></span>
      </div>
      <h2>Certificate installed</h2>
      <p className="smsg">
        Your {provider?.label || "SSL"} certificate for <b>{domain}</b> is active and serving traffic across all Layerd edge
        nodes. Auto-renewal is enabled.
      </p>
      <div className="ld-cert-card">
        <div className="cc-head">
          <div className="cc-ic"><ShieldCheck /></div>
          <div>
            <div className="cc-nm">{domain}</div>
            <div className="cc-sub">{sub}</div>
          </div>
        </div>
        <div className="cc-rows">
          <div className="cc-row">
            <span className="k">Status</span>
            <span className="val">
              <span className="ld-pill" style={{ background: "var(--lsd-success-100)", color: "#1a7a47" }}>
                <span className="d" style={{ background: "var(--lsd-success-500)" }} />Active
              </span>
            </span>
          </div>
          <div className="cc-row">
            <span className="k">Coverage</span>
            <span className="val">{coverage}</span>
          </div>
          <div className="cc-row">
            <span className="k">Issued</span>
            <span className="val"><span className="mono">{fmt(today)}</span></span>
          </div>
          <div className="cc-row">
            <span className="k">Expires</span>
            <span className="val"><span className="mono">{fmt(expiry)}</span></span>
          </div>
          <div className="cc-row">
            <span className="k">Auto-renew</span>
            <span className="val">30 days before expiry</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function providerIdForInitialTab(initialTab, providers) {
  if (initialTab === "upload") {
    const custom = providers?.find((p) => p.id === "custom");
    if (custom?.usable) return "custom";
  }
  const def = providers?.find((p) => p.default && p.usable);
  if (def) return def.id;
  return initialTab === "upload" ? "custom" : "zerossl";
}

function InstallCertDrawer({ open, onClose, siteUid, initialTab = "free" }) {
  const { getCertProviders, provisionSubmit, provisionStatus, provisionVerify,
          provisionCancel, provisionInstall, getSSLConfig } = useSSLConfig();
  const { showSuccess, showError } = useNotification();

  // step:  "pick" | "configure" | "dcv" | "issued"
  // For custom/origin → falls back to the existing helper components.
  const [step, setStep] = React.useState("pick");
  // Pull the canonical site hostname (text) from the global site scope so the
  // disabled Domain field below shows e.g. "*.example.com" rather than the raw
  // siteUid UUID the drawer is wired with.
  const { curSiteId } = useGlobalSite();
  const [providers, setProviders] = React.useState(null);
  const [providerId, setProviderId] = React.useState("zerossl");
  const [sans, setSans] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [orderInfo, setOrderInfo] = React.useState(null); // { status, dcv }
  const [copyState, setCopyState] = React.useState(null);
  const [countdown, setCountdown] = React.useState(15);
  const [verifying, setVerifying] = React.useState(false);
  const pollRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    setStep("pick");
    setOrderInfo(null);
    setSans("");
    setCountdown(15);
    setCopyState(null);
    setProviderId(initialTab === "upload" ? "custom" : "zerossl");
    getCertProviders().then((r) => {
      if (r.status === "success") {
        setProviders(r.data);
        setProviderId(providerIdForInitialTab(initialTab, r.data));
      } else {
        setProviders([]);
        setProviderId(initialTab === "upload" ? "custom" : "zerossl");
      }
    });
  }, [open, initialTab, getCertProviders]);

  React.useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // 15s countdown ring while we're waiting on DCV (and not mid-submit).
  React.useEffect(() => {
    if (step !== "dcv" || submitting) return;
    setCountdown(15);
    const t = setInterval(() => {
      setCountdown((c) => (c <= 1 ? 15 : c - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [step, submitting, orderInfo?.dcv?.records?.length]);

  const startPolling = React.useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const r = await provisionStatus(siteUid);
      if (r.status !== "success") return;
      setOrderInfo(r.data);
      if (r.data?.order_id) await getSSLConfig?.(siteUid, true);
      if (r.data?.status === "issued") {
        clearInterval(pollRef.current); pollRef.current = null;
        const ins = await provisionInstall(siteUid);
        if (ins.status === "success") {
          await getSSLConfig?.(siteUid, true);
          if (ins.data?.draft && !ins.data?.activated) {
            showSuccess("Certificate ready — active certificate keeps serving until activation");
            setOrderInfo({ ...r.data, draft_ready: true });
            setStep("draft_ready");
          } else {
            showSuccess("Certificate installed");
            setStep("issued");
            setTimeout(() => onClose(), 2500);
          }
        } else {
          showError(ins.data?.message || "Install failed");
        }
      } else if (r.data?.status === "failed") {
        clearInterval(pollRef.current); pollRef.current = null;
        showError(`Provisioning failed: ${r.data?.reason || "unknown"}`);
      }
    }, 15000);
  }, [siteUid, provisionStatus, provisionInstall, getSSLConfig, showSuccess, showError, onClose]);

  const onSubmit = async () => {
    setSubmitting(true);
    const sansArr = sans.split(",").map((s) => s.trim()).filter(Boolean);
    // Move to step 3 immediately so the user sees the provisioning spinner
    // while the submit request is in-flight.
    setStep("dcv");
    setOrderInfo(null);
    const r = await provisionSubmit(siteUid, { provider: providerId, sans: sansArr });
    setSubmitting(false);
    if (r.status !== "success") {
      showError(r.data?.message || "Failed to start provisioning");
      // Roll back to the configure step on failure so the user can retry.
      setStep("configure");
      return;
    }
    setOrderInfo(r.data);
    await getSSLConfig?.(siteUid, true);
    if (r.data?.status === "issued") {
      const ins = await provisionInstall(siteUid);
      if (ins.status === "success") {
        await getSSLConfig?.(siteUid, true);
        if (ins.data?.draft && !ins.data?.activated) {
          showSuccess("Certificate ready — active certificate keeps serving until activation");
          setOrderInfo({ ...r.data, draft_ready: true });
          setStep("draft_ready");
        } else {
          showSuccess("Certificate installed");
          setStep("issued");
          setTimeout(() => onClose(), 2500);
        }
      }
    } else {
      startPolling();
    }
  };

  const onVerify = async () => {
    setVerifying(true);
    const r = await provisionVerify(siteUid);
    setVerifying(false);
    if (r.status === "success") {
      setOrderInfo(r.data);
      setCountdown(15);
      showSuccess("Validation re-check sent");
    } else {
      showError(r.data?.message || "Re-check failed");
    }
  };

  const onCancel = async () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if ((orderInfo?.order_id || step === "dcv") && step !== "draft_ready" && step !== "issued") {
      await provisionCancel(siteUid);
    }
    onClose();
  };

  const onCopy = React.useCallback((text, key) => {
    try { navigator.clipboard && navigator.clipboard.writeText(text); } catch (e) { /* noop */ }
    setCopyState(key);
    setTimeout(() => setCopyState(null), 1400);
  }, []);

  const selected = providers?.find((p) => p.id === providerId);
  const isManaged = ["zerossl", "acm", "digicert"].includes(providerId);
  const domainStr = curSiteId ? `*.${curSiteId}` : "";

  // Map the existing `pick / configure / dcv / issued` step machine onto
  // the design's 4-step numbered rail.
  const railStep = step === "pick" ? 1
                 : step === "configure" ? 2
                 : step === "dcv" ? 3
                 : 4;
  const dcvIssued = orderInfo?.status === "issued";
  const verifiedPhase = dcvIssued && !orderInfo?.draft_ready;
  const provisioningPhase = step === "dcv" && (submitting || !orderInfo || !(orderInfo?.dcv?.records?.length));

  // Header sub-line (unchanged copy from the previous drawer)
  const subLine = step === "pick"
    ? "Pick a provider — Layerd absorbs the cost."
    : step === "configure"
      ? `Configure ${selected?.label || "certificate"} request`
        : step === "dcv"
        ? (selected?.label
            ? `Validate with ${selected.label} — publish DNS records below`
            : "Publish the DNS records below to validate ownership")
        : step === "draft_ready"
          ? "Certificate ready — waiting for activation"
          : "Certificate installed";

  // Footer renderer
  const renderFooter = () => {
    if (step === "pick") {
      return (
        <div className="ld-dw-foot">
          <div className="ld-foot-status">
            {selected?.usable && (<><span className="label">Selected ·</span><span className="strong">{selected.label}</span></>)}
          </div>
          <div className="ld-foot-grow" />
          <button type="button" className="ld-btn ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="ld-btn primary" onClick={() => setStep("configure")} disabled={!selected?.usable}>
            Continue<ArrowRight />
          </button>
        </div>
      );
    }
    if (step === "configure") {
      // Custom / origin hand off to their own forms — the existing
      // SSLConfigurationUpload / SSLConfigurationGenerateOrigin components
      // own their footer buttons, so we render only Back + status here.
      if (!isManaged) {
        return (
          <div className="ld-dw-foot">
            <div className="ld-foot-status"><span className="label">Configure ·</span><span className="strong">{selected?.label}</span></div>
            <div className="ld-foot-grow" />
            <button type="button" className="ld-btn ghost" onClick={() => setStep("pick")}>Back</button>
          </div>
        );
      }
      return (
        <div className="ld-dw-foot">
          <div className="ld-foot-status"><span className="label">Configure ·</span><span className="strong">{selected?.label} request</span></div>
          <div className="ld-foot-grow" />
          <button type="button" className="ld-btn ghost" onClick={() => setStep("pick")}>Back</button>
          <button type="button" className="ld-btn primary" onClick={onSubmit} disabled={submitting}>
            {submitting ? (<><span className="btn-spin" />Starting</>) : (<>Start provisioning<ArrowRight /></>)}
          </button>
        </div>
      );
    }
    if (step === "dcv") {
      if (provisioningPhase) {
        return (
          <div className="ld-dw-foot">
            <div className="ld-foot-status"><span className="strong">Ordering certificate…</span></div>
            <div className="ld-foot-grow" />
            <button type="button" className="ld-btn ghost" disabled>Cancel order</button>
            <button type="button" className="ld-btn primary" disabled><span className="btn-spin" />Provisioning</button>
          </div>
        );
      }
      return (
        <div className="ld-dw-foot">
          <div className="ld-foot-status">
            {orderInfo?.order_id ? (
              <><span className="label">Order ·</span><span className="strong mono">{orderInfo.order_id.slice(0, 24)}{orderInfo.order_id.length > 24 ? "…" : ""}</span></>
            ) : null}
          </div>
          <div className="ld-foot-grow" />
          <button type="button" className="ld-btn danger-ghost" onClick={onCancel}>Cancel order</button>
          <button
            type="button"
            className="ld-btn primary"
            onClick={verifiedPhase ? () => setStep("issued") : onVerify}
            disabled={verifying}
          >
            {verifying ? (<><span className="btn-spin" />Checking</>)
              : verifiedPhase ? (<>Finish<ArrowRight /></>)
              : (<><RotateCw />Check now</>)}
          </button>
        </div>
      );
    }
    if (step === "draft_ready") {
      return (
        <div className="ld-dw-foot">
          <div className="ld-foot-status"><CheckCircle2 style={{ color: "var(--lsd-success-500)" }} /><span className="strong">Draft ready</span></div>
          <div className="ld-foot-grow" />
          <button type="button" className="ld-btn primary" onClick={onCancel}>Done</button>
        </div>
      );
    }
    // step === "issued"
    return (
      <div className="ld-dw-foot">
        <div className="ld-foot-status"><CheckCircle2 style={{ color: "var(--lsd-success-500)" }} /><span className="strong">Certificate active</span></div>
        <div className="ld-foot-grow" />
        <button type="button" className="ld-btn ghost" onClick={onCancel}>View certificate</button>
        <button type="button" className="ld-btn primary" onClick={onCancel}>Done</button>
      </div>
    );
  };

  return (
    <Modal open={open} onClose={onCancel}>
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Box sx={{
          position: "fixed", top: 0, right: 0,
          width: 600, maxWidth: "100vw", height: "100vh",
          backgroundColor: "var(--lsd-bg-surface, #fff)",
          boxShadow: "-24px 0 60px rgba(17,17,17,0.16)",
          zIndex: 1300,
          display: "flex", flexDirection: "column",
        }}>
          <div className="layerd-ssl-drawer">
            <div className="ld-dw-head">
              <div className="ld-dw-mark"><FileLock2 /></div>
              <div className="ld-dw-titles">
                <div style={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}>
                  <h1>Install SSL certificate</h1>
                  <FormHelpTooltip tooltip="Install or replace the TLS certificate for this site. Choose a managed provider (free Layerd cert), upload your own, or generate an origin certificate for encrypting traffic to your backend." />
                </div>
                <div className="sub">{subLine}</div>
              </div>
              <button type="button" className="ld-dw-close" onClick={onCancel} aria-label="Close"><LucideX /></button>
            </div>

            <Rail step={railStep} />

            <div className="ld-dw-body">
              {step === "pick" && (
                <StepProvider providers={providers} providerId={providerId} setProviderId={setProviderId} />
              )}

              {step === "configure" && isManaged && (
                <StepConfigure
                  provider={selected}
                  domain={domainStr}
                  sans={sans}
                  setSans={setSans}
                  goProvider={() => setStep("pick")}
                />
              )}

              {/* Custom / origin paths still hand off to their existing form
                  components — they own their own buttons + submit flow. */}
              {step === "configure" && providerId === "custom" && (
                <div style={{ padding: 4 }}>
                  <SSLConfigurationUpload open={open} siteUid={siteUid} handleClose={onClose} />
                </div>
              )}
              {step === "configure" && providerId === "origin" && (
                <div style={{ padding: 4 }}>
                  <SSLConfigurationGenerateOrigin open={open} siteUid={siteUid} handleClose={onClose} />
                </div>
              )}

              {step === "dcv" && provisioningPhase && (
                <StepProvisioningSpinner provider={selected} orderInfo={orderInfo} />
              )}
              {step === "dcv" && !provisioningPhase && (
                <StepValidate
                  provider={selected}
                  orderInfo={orderInfo}
                  verified={verifiedPhase}
                  countdown={countdown}
                  copyState={copyState}
                  onCopy={onCopy}
                />
              )}

              {step === "draft_ready" && (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "var(--lsd-success-500)", mb: 2 }} />
                  <Typography sx={{ ...certsSectionTitleSx, mb: 1 }}>Certificate ready</Typography>
                  <Typography sx={{ ...certsBodySecondarySx }}>
                    Your new certificate is stored as a draft. The active certificate keeps serving
                    until you activate the draft manually or it activates automatically when the
                    current certificate expires.
                  </Typography>
                </Box>
              )}

              {step === "issued" && (
                <StepSuccess domain={domainStr} sans={sans} provider={selected} />
              )}
            </div>

            {renderFooter()}
          </div>
        </Box>
      </Slide>
    </Modal>
  );
}

// ─── RECOMMENDATIONS (derived from sslConfig + rating) ─────────────
function buildRecommendations(sslConfig, sslRating) {
  const recs = [];
  // 1. Encryption mode
  if (sslConfig?.ssl_type === SslType.OFF || sslConfig?.ssl_type == null) {
    recs.push({ tone: "warn", icon: <ShieldOutlinedIcon sx={{ fontSize: 15 }} />,
      title: "Turn on encryption",
      desc: "Site is currently in InSecure mode. Visitors and origin traffic are unencrypted.",
      action: "Fix",
      actionKey: "fix-encryption" });
  } else if (sslConfig?.ssl_type < SslType.FULL_STRICT) {
    recs.push({ tone: "warn", icon: <LockOutlinedIcon sx={{ fontSize: 15 }} />,
      title: "Switch to Advanced E2E",
      desc: "Validate the origin certificate to prevent MITM between Layerd and your backend.",
      action: "Review",
      actionKey: "review-advanced-e2e" });
  }
  // 2. Min TLS
  if (typeof sslConfig?.min_tls_version === "number" && sslConfig.min_tls_version < TlsVersion.TLS_1_3) {
    recs.push({ tone: "info", icon: <TrendingUpIcon sx={{ fontSize: 15 }} />,
      title: "Raise minimum TLS to v1.3",
      desc: "TLS 1.2 is rarely used by modern clients; bumping the floor improves your score.",
      action: "Apply",
      actionKey: "apply-min-tls" });
  }
  // 3. HSTS preload
  if (!sslConfig?.hsts?.preload && sslConfig?.hsts?.enabled) {
    recs.push({ tone: "info", icon: <StarIcon sx={{ fontSize: 15 }} />,
      title: "Enable HSTS preload",
      desc: "Submit your domain to the HSTS preload list so browsers enforce HTTPS by default.",
      action: "Enable",
      actionKey: "enable-hsts-preload" });
  } else if (sslConfig?.hsts?.preload) {
    recs.push({ tone: "ok", icon: <CheckIcon sx={{ fontSize: 15 }} />,
      title: "HSTS preload enabled",
      desc: "Great. Your domain is queued for the browser preload list.",
      action: null });
  }
  // 4. HTTPS redirect
  if (!sslConfig?.https_redirect_enabled) {
    recs.push({ tone: "warn", icon: <RedoIcon sx={{ fontSize: 15 }} />,
      title: "Enable HTTP → HTTPS redirect",
      desc: "Plain HTTP traffic is not being redirected, allowing insecure connections.",
      action: "Enable",
      actionKey: "enable-https-redirect" });
  }
  // 5. Last scan error
  if (sslRating?.last_scan_error && sslRating?.grade) {
    recs.push({ tone: "warn", icon: <WarningAmberIcon sx={{ fontSize: 15 }} />,
      title: "Last scan reported issues",
      desc: sslRating.last_scan_error,
      action: "Rescan",
      actionKey: "rescan" });
  }
  return recs;
}

function RecommendationsCard({ sslConfig, sslRating, onRescan, onAction }) {
  const recs = buildRecommendations(sslConfig, sslRating);
  const handleAction = (r) => {
    if (r.actionKey === "rescan") onRescan?.();
    else onAction?.(r.actionKey);
  };
  return (
    <Card
      icon={<StarIcon sx={{ fontSize: 17, color: INDIGO }} />}
      title="Improve your rating"
      action={sslRating?.grade && sslRating.grade !== "A+" ? <Pill tone="warn">{sslRating.grade} → A+ possible</Pill> : null}
    >
      {recs.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center", color: colors.neutral[600], ...certsBodySmallSx }}>
          Everything looks good — no recommendations.
        </Box>
      ) : recs.map((r, i) => {
        const tones = {
          ok:   { bg: "rgba(31,193,107,0.10)", fg: GREEN_DARK },
          warn: { bg: "rgba(255,219,67,0.18)", fg: "#7a5b10" },
          info: { bg: INDIGO_SOFT,             fg: INDIGO_INK },
        };
        const t = tones[r.tone] || tones.info;
        return (
          <Stack
            key={i}
            direction="row"
            alignItems="flex-start"
            sx={{ ...certsRecItemSx, borderBottom: i === recs.length - 1 ? 0 : certsRecItemSx.borderBottom }}
          >
            <Box sx={{
              width: 30, height: 30, borderRadius: "8px",
              backgroundColor: t.bg, color: t.fg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>{r.icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ ...certsRowTitleSx, mb: 0.25 }}>{r.title}</Typography>
              <Typography sx={{ ...certsBodySecondarySx }}>{r.desc}</Typography>
            </Box>
            {r.action && (
              <GhostButton
                sx={{ ...ghostButtonSmSx, alignSelf: "center" }}
                onClick={() => handleAction(r)}
              >
                {r.action}
              </GhostButton>
            )}
          </Stack>
        );
      })}
    </Card>
  );
}

// ─── CERTIFICATE HISTORY (current cert only — no historical API yet) ─
function CertificateHistory({ sslConfig }) {
  const cert = sslConfig?.certs;
  const typeLabel =
    cert?.type === CertificateType.SENSE_GUARD   ? "Universal"
    : cert?.type === CertificateType.CUSTOM      ? "Custom"
    : cert?.type === CertificateType.LAYERD_MANAGED ? "Layerd managed"
    :                                              "Certificate";
  const expired = cert?.validTo ? new Date(cert.validTo) < new Date() : false;
  return (
    <Card
      icon={<HistoryIcon sx={{ fontSize: 16, color: INDIGO }} />}
      title="Certificate history"
    >
      {cert && cert.host?.length ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ ...certsSettingRowSx, borderBottom: 0 }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ ...certsBodySmallSx, fontWeight: 500, fontSize: 13.5 }}>
              {Array.isArray(cert.host) ? cert.host[0] : cert.host}
            </Typography>
            <Typography sx={{ ...certsBodySecondarySx, mt: 0.25 }}>
              {typeLabel}
              {cert.validTo ? ` · expires ${formatDate(cert.validTo)}` : ""}
            </Typography>
          </Box>
          {expired
            ? <Pill tone="danger"><Box sx={dotSx} />Expired</Pill>
            : <Pill tone="ok"><Box sx={dotSx} />Active</Pill>}
        </Stack>
      ) : (
        <Box sx={{ p: 3, textAlign: "center", color: colors.neutral[600], fontSize: 13 }}>
          No certificate installed yet.
        </Box>
      )}
      <Box sx={{
        backgroundColor: FAINT_GREY,
        borderTop: `1px solid ${colors.neutral[200]}`,
        p: "14px 20px",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
      }}>
        <InfoIcon sx={{ fontSize: 14, color: colors.neutral[600] }} />
        <Typography sx={certsBodySecondarySx}>
          Historical certificate records are not retained by the SSL config API yet — only
          the active certificate is shown.
        </Typography>
      </Box>
    </Card>
  );
}

// ─── Custom TLS profile rows ───────────────────────────────────────
// Rendered only when tls_policy_preset === custom. The backend Joi
// validator rejects:
//   - max < min when both set
//   - cipher_list with fewer than TLS_MIN_CIPHER_COUNT_TLS12 entries
//   - any cipher/suite outside the catalog
// We surface those errors via the showError toast from onCustomTlsField.
function CustomTlsRows({ sslConfig, onCustomTlsField, disabled }) {
  const ciphers   = parseCipherString(sslConfig?.cipher_list);
  const suites    = parseCipherString(sslConfig?.ciphersuites);
  const minV      = sslConfig?.min_tls_version ?? TlsVersion.TLS_1_2;
  const maxV      = sslConfig?.max_tls_version ?? TlsVersion.TLS_1_3;
  const allowTls12 = minV <= TlsVersion.TLS_1_2;
  return (
    <>
      <SettingRow
        name="Maximum TLS version"
        desc="Top of the negotiation window. Connections offering higher versions are dropped to this ceiling."
        control={
          <Select size="small" value={maxV}
            onChange={(e) => onCustomTlsField({ max_tls_version: Number(e.target.value) })}
            disabled={disabled} sx={miniSelectSx}>
            <MenuItem value={TlsVersion.TLS_1_2}>TLS v1.2</MenuItem>
            <MenuItem value={TlsVersion.TLS_1_3}>TLS v1.3</MenuItem>
          </Select>
        }
      />
      {allowTls12 && (
        <SettingRow
          name="TLS 1.2 ciphers"
          desc={`OpenSSL cipher list for the TLS 1.2 negotiation window. Server enforces a minimum of ${TLS_MIN_CIPHER_COUNT_TLS12} entries.`}
          control={
            <Select size="small" multiple value={ciphers}
              onChange={(e) => onCustomTlsField({
                cipher_list: typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value,
              })}
              disabled={disabled}
              renderValue={(sel) =>
                `${sel.length} cipher${sel.length === 1 ? "" : "s"}${
                  sel.length < TLS_MIN_CIPHER_COUNT_TLS12 ? ` (need ≥${TLS_MIN_CIPHER_COUNT_TLS12})` : ""
                }`}
              sx={{ ...miniSelectSx, width: 220, minWidth: 220 }}>
              {TLS12_CIPHERS.map((c) => (
                <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>
                  <Checkbox checked={ciphers.indexOf(c) > -1} size="small"
                    sx={{ color: colors.neutral[400], "&.Mui-checked": { color: INDIGO } }} />
                  <ListItemText primaryTypographyProps={ { fontSize: 12 }} primary={c} />
                </MenuItem>
              ))}
            </Select>
          }
        />
      )}
      <SettingRow
        name="TLS 1.3 ciphersuites"
        desc="AEAD suites for TLS 1.3. Order is preference order; server picks the leftmost the client offers."
        control={
          <Select size="small" multiple value={suites}
            onChange={(e) => onCustomTlsField({
              ciphersuites: typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value,
            })}
            disabled={disabled}
            renderValue={(sel) => `${sel.length} suite${sel.length === 1 ? "" : "s"}`}
            sx={{ ...miniSelectSx, width: 220, minWidth: 220 }}>
            {TLS13_CIPHERS.map((c) => (
              <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>
                <Checkbox checked={suites.indexOf(c) > -1} size="small"
                  sx={{ color: colors.neutral[400], "&.Mui-checked": { color: INDIGO } }} />
                <ListItemText primaryTypographyProps={ { fontSize: 12 }} primary={c} />
              </MenuItem>
            ))}
          </Select>
        }
      />
      {/* PFS status badge — derived from the current selection. */}
      <SettingRow
        last
        name="Forward secrecy"
        desc="Layerd's TLS catalog only allows ECDHE / DHE / TLS 1.3 ciphers, so PFS is enforced — this row makes it explicit in Custom mode where an operator could theoretically narrow the cipher list."
        control={
          (() => {
            const t13Only = minV >= TlsVersion.TLS_1_3;
            const t12Pfs  = ciphers.length > 0 && ciphers.every((c) => /^(ECDHE|DHE)-/i.test(c));
            const ok = t13Only || t12Pfs;
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: ok ? "#0f766e" : "#b45309" }} />
                <Typography sx={ { fontSize: 12, color: ok ? "#0f766e" : "#b45309", fontWeight: 600 }}>
                  {ok ? (t13Only ? "Enabled (TLS 1.3 only — PFS mandatory)" : "Enabled (all selected TLS 1.2 ciphers are ECDHE/DHE)") : "Review cipher selection"}
                </Typography>
              </Stack>
            );
          })()
        }
      />
    </>
  );
}

const miniSelectSx = {
  height: 34,
  width: 180,
  minWidth: 180,
  ...certsBodySmallSx,
  fontWeight: 500,
  backgroundColor: colors.neutral[100],
  "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.neutral[200], borderRadius: "8px" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.neutral[400] },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: INDIGO },
};

const TLS_VERSION_LABELS = {
  [TlsVersion.TLS_1_0]: "TLS v1.0",
  [TlsVersion.TLS_1_1]: "TLS v1.1",
  [TlsVersion.TLS_1_2]: "TLS v1.2",
  [TlsVersion.TLS_1_3]: "TLS v1.3",
  [TlsVersion.TLS_1_4]: "TLS v1.4",
};

const TLS_PROFILE_LABELS = {
  [TlsProfile.INTERMEDIATE]: "Intermediate",
  [TlsProfile.MODERN]: "Modern",
  [TlsProfile.CUSTOM]: "Custom",
};

const HSTS_MAX_AGE_LABELS = {
  300: "5 minutes",
  86400: "1 day",
  604800: "1 week",
  2592000: "1 month",
  15552000: "6 months",
  31536000: "1 year (preload)",
};

function sslModeTitle(id) {
  return SSL_MODES.find((m) => m.id === id)?.title ?? String(id);
}

function tlsVersionLabel(v) {
  return TLS_VERSION_LABELS[v] ?? String(v);
}

function tlsProfileLabel(v) {
  return TLS_PROFILE_LABELS[v] ?? String(v);
}

function enabledLabel(v) {
  return v ? "Enabled" : "Disabled";
}

function hstsMaxAgeLabel(v) {
  return HSTS_MAX_AGE_LABELS[v] ?? `${v}s`;
}

function fieldChange(label, from, to) {
  return `${label}: ${from} → ${to}`;
}

function collectPlainModifiedFields(baseline, draft) {
  if (!baseline || !draft) return [];
  const fields = [];

  if (baseline.ssl_type !== draft.ssl_type) {
    fields.push(fieldChange("Encryption mode", sslModeTitle(baseline.ssl_type), sslModeTitle(draft.ssl_type)));
  }
  if (baseline.https_redirect_enabled !== draft.https_redirect_enabled) {
    fields.push(fieldChange("HTTP → HTTPS redirection", enabledLabel(baseline.https_redirect_enabled), enabledLabel(draft.https_redirect_enabled)));
  }
  if (baseline.www_redirect_enabled !== draft.www_redirect_enabled) {
    fields.push(fieldChange("Force www redirect", enabledLabel(baseline.www_redirect_enabled), enabledLabel(draft.www_redirect_enabled)));
  }
  if (baseline.http_rewrite_enabled !== draft.http_rewrite_enabled) {
    fields.push(fieldChange("Automatic HTTP rewrites", enabledLabel(baseline.http_rewrite_enabled), enabledLabel(draft.http_rewrite_enabled)));
  }
  if (baseline.min_tls_version !== draft.min_tls_version) {
    fields.push(fieldChange("Minimum TLS version", tlsVersionLabel(baseline.min_tls_version), tlsVersionLabel(draft.min_tls_version)));
  }
  if (baseline.tls_policy_preset !== draft.tls_policy_preset) {
    fields.push(fieldChange("TLS profile", tlsProfileLabel(baseline.tls_policy_preset), tlsProfileLabel(draft.tls_policy_preset)));
  }
  if (baseline.max_tls_version !== draft.max_tls_version) {
    fields.push(fieldChange("Maximum TLS version", tlsVersionLabel(baseline.max_tls_version), tlsVersionLabel(draft.max_tls_version)));
  }
  if (baseline.cipher_list !== draft.cipher_list) {
    fields.push("TLS 1.2 ciphers");
  }
  if (baseline.ciphersuites !== draft.ciphersuites) {
    fields.push("TLS 1.3 ciphersuites");
  }

  const baseHsts = baseline.hsts || {};
  const draftHsts = draft.hsts || {};
  if (baseHsts.enabled !== draftHsts.enabled) {
    fields.push(fieldChange("Enable HSTS", enabledLabel(baseHsts.enabled), enabledLabel(draftHsts.enabled)));
  }
  if (baseHsts.max_age !== draftHsts.max_age) {
    fields.push(fieldChange("HSTS max age", hstsMaxAgeLabel(baseHsts.max_age), hstsMaxAgeLabel(draftHsts.max_age)));
  }
  if (baseHsts.include_sub_domains !== draftHsts.include_sub_domains) {
    fields.push(fieldChange("Include subdomains", enabledLabel(baseHsts.include_sub_domains), enabledLabel(draftHsts.include_sub_domains)));
  }
  if (baseHsts.preload !== draftHsts.preload) {
    fields.push(fieldChange("HSTS preload", enabledLabel(baseHsts.preload), enabledLabel(draftHsts.preload)));
  }
  if (baseHsts.no_sniff_header !== draftHsts.no_sniff_header) {
    fields.push(fieldChange("No-Sniff header", enabledLabel(baseHsts.no_sniff_header), enabledLabel(draftHsts.no_sniff_header)));
  }

  return fields;
}

// ─── MAIN PAGE BODY ────────────────────────────────────────────────
export default function SSLTLSPage({ siteUid, installTrigger = 0, onInstallConsumed }) {
  const { sslConfig, sslRating, sslRatingLoading, sslRatingProgress,
          sslTypeChange, configSslSetting, getSSLConfig, getLatestSSLRating } = useSSLConfig();

  // Fetch the latest SSL Labs rating for this site on mount and whenever
  // the site changes. Without this the hero "Overall rating" cell stays
  // empty until the user manually clicks Refresh — sslRating is a
  // separate piece of state from sslConfig and isn't populated by
  // getSSLConfig.
  React.useEffect(() => {
    if (siteUid && getLatestSSLRating) {
      getLatestSSLRating(siteUid).catch(() => { /* surface via toast inside the helper */ });
    }
  }, [siteUid, getLatestSSLRating]);
  const { userType } = useAuth();
  const { showSuccess, showError } = useNotification();
  const disabled = userType === UserType.ORG_USER;

  const pickPlainDraft = React.useCallback((cfg) => {
    if (!cfg) return null;
    return {
      ssl_type: cfg.ssl_type,
      https_redirect_enabled: !!cfg.https_redirect_enabled,
      www_redirect_enabled: !!cfg.www_redirect_enabled,
      http_rewrite_enabled: !!cfg.http_rewrite_enabled,
      min_tls_version: cfg.min_tls_version ?? TlsVersion.TLS_1_2,
      tls_policy_preset: cfg.tls_policy_preset || TlsProfile.INTERMEDIATE,
      max_tls_version: cfg.max_tls_version ?? TlsVersion.TLS_1_3,
      cipher_list: cfg.cipher_list,
      ciphersuites: cfg.ciphersuites,
      hsts: {
        enabled: !!cfg?.hsts?.enabled,
        max_age: cfg?.hsts?.max_age || 15552000,
        include_sub_domains: !!cfg?.hsts?.include_sub_domains,
        preload: !!cfg?.hsts?.preload,
        no_sniff_header: !!cfg?.hsts?.no_sniff_header,
      },
    };
  }, []);

  const [plainBaseline, setPlainBaseline] = React.useState(null);
  const [plainDraft, setPlainDraft] = React.useState(null);
  const [isApplyingPlain, setIsApplyingPlain] = React.useState(false);

  React.useEffect(() => {
    const next = pickPlainDraft(sslConfig);
    setPlainBaseline(next);
    setPlainDraft(next);
  }, [pickPlainDraft, sslConfig]);

  const [installOpen, setInstallOpen] = React.useState(false);
  const [installTab, setInstallTab] = React.useState("free");
  const [compareOpen, setCompareOpen] = React.useState(false);
  const [compareMode, setCompareMode] = React.useState(SslType.FULL_STRICT);
  const handledInstallTriggerRef = React.useRef(installTrigger);

  React.useEffect(() => {
    if (installTrigger === 0) {
      handledInstallTriggerRef.current = 0;
      return;
    }
    if (installTrigger <= handledInstallTriggerRef.current) return;
    handledInstallTriggerRef.current = installTrigger;
    setInstallTab("free");
    setInstallOpen(true);
    onInstallConsumed?.();
  }, [installTrigger, onInstallConsumed]);

  const onSelectMode = (newType) => {
    if (disabled || !plainDraft) return;
    if (newType === plainDraft.ssl_type) return;
    setPlainDraft({ ...plainDraft, ssl_type: newType });
  };

  const onToggle = (name, value) => {
    if (disabled || !plainDraft) return;
    const enable = !!value?.enable;
    if (name === "httpsRedirectChange") setPlainDraft({ ...plainDraft, https_redirect_enabled: enable });
    else if (name === "wwwRedirectChange") setPlainDraft({ ...plainDraft, www_redirect_enabled: enable });
    else if (name === "autoHttpRewriteChange") setPlainDraft({ ...plainDraft, http_rewrite_enabled: enable });
    else if (name === "hstsSettingChange") setPlainDraft({ ...plainDraft, hsts: { ...(plainDraft.hsts || {}), enabled: enable } });
  };

  const onTlsVersion = (v) => {
    if (disabled || !plainDraft) return;
    setPlainDraft({ ...plainDraft, min_tls_version: v });
  };

  const onTlsProfile = (preset) => {
    if (disabled || !plainDraft) return;
    setPlainDraft({ ...plainDraft, tls_policy_preset: preset });
  };

  // Custom TLS knobs — fired through tlsProfileChange with preset=custom
  // so the backend re-validates against the catalog on every patch.
  const onCustomTlsField = async (patch) => {
    if (disabled || !plainDraft) return;
    setPlainDraft({
      ...plainDraft,
      tls_policy_preset: TlsProfile.CUSTOM,
      min_tls_version: plainDraft.min_tls_version,
      max_tls_version: plainDraft.max_tls_version,
      cipher_list: plainDraft.cipher_list,
      ciphersuites: plainDraft.ciphersuites,
      ...patch,
    });
  };

  const onHstsUpdate = (patch) => {
    if (disabled || !plainDraft) return;
    setPlainDraft({ ...plainDraft, hsts: { ...(plainDraft.hsts || {}), ...patch } });
  };

  const onRescan = async () => {
    try {
      if (getLatestSSLRating) await getLatestSSLRating(siteUid, true);
    } catch (e) { /* ignore */ }
  };

  const scrollTo = (elementId) => {
    requestAnimationFrame(() => {
      document.getElementById(elementId)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    });
  };

  const onRecommendationAction = React.useCallback((actionKey) => {
    if (disabled || !plainDraft) return;
    switch (actionKey) {
      case "review-advanced-e2e":
        onSelectMode(SslType.FULL_STRICT);
        scrollTo("ssl-mode-ladder");
        break;
      case "apply-min-tls":
        onTlsVersion(TlsVersion.TLS_1_3);
        scrollTo("ssl-protocol-cipher");
        break;
      case "fix-encryption":
        onSelectMode(SslType.FLEXIBLE);
        scrollTo("ssl-mode-ladder");
        break;
      case "enable-hsts-preload":
        onHstsUpdate({ preload: true });
        break;
      case "enable-https-redirect":
        onToggle("httpsRedirectChange", { enable: true });
        break;
      default:
        break;
    }
  }, [disabled, plainDraft, onSelectMode, onTlsVersion, onHstsUpdate, onToggle]);

  const sslConfigForRender = React.useMemo(() => {
    if (!sslConfig) return null;
    if (!plainDraft) return sslConfig;
    return { ...sslConfig, ...plainDraft, hsts: { ...(sslConfig.hsts || {}), ...(plainDraft.hsts || {}) } };
  }, [plainDraft, sslConfig]);

  const hstsEnabled = !!sslConfigForRender?.hsts?.enabled;
  const hstsChildDisabled = disabled || !hstsEnabled;

  const plainModifiedFields = React.useMemo(
    () => collectPlainModifiedFields(plainBaseline, plainDraft),
    [plainBaseline, plainDraft],
  );
  const plainModifiedCount = plainModifiedFields.length;

  const discardPlain = React.useCallback(() => {
    if (!plainBaseline) return;
    setPlainDraft(plainBaseline);
  }, [plainBaseline]);

  const applyPlain = React.useCallback(async () => {
    if (disabled || !siteUid || !plainBaseline || !plainDraft) return;
    setIsApplyingPlain(true);
    try {
      if (plainDraft.ssl_type !== plainBaseline.ssl_type) {
        await sslTypeChange(siteUid, plainDraft.ssl_type);
      }
      if (plainDraft.https_redirect_enabled !== plainBaseline.https_redirect_enabled) {
        await configSslSetting(siteUid, "httpsRedirectChange", { enable: plainDraft.https_redirect_enabled });
      }
      if (plainDraft.www_redirect_enabled !== plainBaseline.www_redirect_enabled) {
        await configSslSetting(siteUid, "wwwRedirectChange", { enable: plainDraft.www_redirect_enabled });
      }
      if (plainDraft.http_rewrite_enabled !== plainBaseline.http_rewrite_enabled) {
        await configSslSetting(siteUid, "autoHttpRewriteChange", { enable: plainDraft.http_rewrite_enabled });
      }

      if (plainDraft.min_tls_version !== plainBaseline.min_tls_version) {
        await configSslSetting(siteUid, "tlsVersionChange", { version: plainDraft.min_tls_version });
      }

      const customPayloadChanged = (
        plainDraft.tls_policy_preset === TlsProfile.CUSTOM
        && (
          plainDraft.tls_policy_preset !== plainBaseline.tls_policy_preset
          || plainDraft.max_tls_version !== plainBaseline.max_tls_version
          || plainDraft.cipher_list !== plainBaseline.cipher_list
          || plainDraft.ciphersuites !== plainBaseline.ciphersuites
        )
      );

      if (customPayloadChanged) {
        await configSslSetting(siteUid, "tlsProfileChange", {
          preset: TlsProfile.CUSTOM,
          min_tls_version: plainDraft.min_tls_version,
          max_tls_version: plainDraft.max_tls_version,
          cipher_list: parseCipherString(plainDraft.cipher_list),
          ciphersuites: parseCipherString(plainDraft.ciphersuites),
        });
      } else if (plainDraft.tls_policy_preset !== plainBaseline.tls_policy_preset) {
        await configSslSetting(siteUid, "tlsProfileChange", { preset: plainDraft.tls_policy_preset });
      }

      if ((plainDraft.hsts || {}).enabled !== (plainBaseline.hsts || {}).enabled) {
        await configSslSetting(siteUid, "hstsSettingChange", { enable: !!plainDraft.hsts?.enabled });
      }
      const hKeys = ["max_age", "include_sub_domains", "preload", "no_sniff_header"];
      const hstsUpdateChanged = hKeys.some((k) => (plainDraft.hsts || {})[k] !== (plainBaseline.hsts || {})[k]);
      if (hstsUpdateChanged) {
        await configSslSetting(siteUid, "HSTSUpdate", plainDraft.hsts);
      }

      showSuccess("Saved");
      if (getSSLConfig) await getSSLConfig(siteUid);
    } catch (e) {
      showError(e?.message || "Failed to save changes");
    } finally {
      setIsApplyingPlain(false);
    }
  }, [configSslSetting, disabled, getSSLConfig, plainBaseline, plainDraft, showError, showSuccess, siteUid, sslTypeChange]);

  const activeMode = SSL_MODES.find((m) => m.id === sslConfigForRender?.ssl_type) || SSL_MODES[0];
  const compareModeMeta = SSL_MODES.find((m) => m.id === compareMode) || activeMode;

  const openCompareModes = React.useCallback(() => {
    setCompareMode(sslConfigForRender?.ssl_type ?? SslType.FULL_STRICT);
    setCompareOpen(true);
  }, [sslConfigForRender?.ssl_type]);

  return (
    <Stack spacing={PAGE_SECTION_SPACING}>
      <HeroStrip
        sslConfig={sslConfig}
        sslRating={sslRating}
        sslRatingLoading={sslRatingLoading}
        sslRatingProgress={sslRatingProgress}
      />

      <Box component="section" sx={{ overflow: "visible" }}>
        <SectionHeader
          icon={<ShieldOutlinedIcon sx={{ fontSize: 18, color: INDIGO }} />}
          title="Encryption mode"
          description="Choose how traffic is encrypted between visitors, Layerd, and your origin."
        />
        <ModeLadder
          selectedSslType={sslConfigForRender?.ssl_type}
          appliedSslType={plainBaseline?.ssl_type ?? sslConfig?.ssl_type}
          onSelect={onSelectMode}
          disabled={disabled}
        />
        {sslConfigForRender?.ssl_type !== undefined && (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{
              mt: MODE_INFO_BANNER_MT,
              p: "14px 18px",
              borderRadius: "10px",
              backgroundColor: INFO_BANNER_BG,
              border: `1px solid ${INDIGO_BORDER}`,
            }}
          >
            <InfoIcon sx={{ fontSize: 18, color: INDIGO, mt: 0.15, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ ...certsBodySmallSx, lineHeight: "20px" }}>
                <Box component="span" sx={{ fontWeight: 600 }}>{activeMode.title}.</Box>{" "}
                {activeMode.desc}
                {activeMode.bestFor && (
                  <>
                    {" "}
                    <Box component="span" sx={{ color: colors.neutral[700] }}>
                      Best for {activeMode.bestFor}
                    </Box>
                  </>
                )}
              </Typography>
              <Typography
                component="span"
                onClick={openCompareModes}
                sx={{
                  display: "inline-block",
                  mt: 1,
                  ...certsBodySmallSx,
                  fontWeight: 500,
                  color: INDIGO,
                  cursor: "pointer",
                  "&:hover": { color: INDIGO_INK, textDecoration: "underline" },
                }}
              >
                Compare modes →
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: `minmax(0, 1fr) ${RIGHT_RAIL_WIDTH}px` },
          gap: GRID_GAP,
          alignItems: "start",
        }}
      >
        <Stack spacing={INNER_STACK_SPACING} sx={{ minWidth: 0 }}>
            <ActiveCertificate sslConfig={sslConfigForRender} />
            <DraftCertificate sslConfig={sslConfigForRender} siteUid={siteUid} disabled={disabled} />

            {/* Protocol & cipher */}
            <SectionedCard
              id="ssl-protocol-cipher"
              icon={<MemoryIcon sx={{ fontSize: 14 }} />}
              title="Protocol & cipher"
              desc="Control which TLS versions and cipher suites are accepted."
            >
              <SettingRow
                name="Minimum TLS version"
                desc="Reject connections older than this version."
                control={
                  <Select size="small" value={sslConfigForRender?.min_tls_version ?? TlsVersion.TLS_1_2}
                    onChange={(e) => onTlsVersion(Number(e.target.value))} sx={miniSelectSx}>
                    <MenuItem value={TlsVersion.TLS_1_0}>TLS v1.0</MenuItem>
                    <MenuItem value={TlsVersion.TLS_1_1}>TLS v1.1</MenuItem>
                    <MenuItem value={TlsVersion.TLS_1_2}>TLS v1.2</MenuItem>
                    <MenuItem value={TlsVersion.TLS_1_3}>TLS v1.3</MenuItem>
                  </Select>
                }
              />
              <SettingRow
                name="TLS profile"
                desc={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <span>Modern / Intermediate / Custom — controls cipher suite negotiation.</span>
                    <Tooltip arrow placement="top"
                      componentsProps={{ tooltip: { sx: { maxWidth: 360, fontSize: 12, lineHeight: "18px", p: 1.5, backgroundColor: "#1F2937", "& .MuiTooltip-arrow": { color: "#1F2937" } } } }}
                      title="Every cipher in Layerd’s TLS catalog provides forward secrecy — ECDHE / DHE for TLS 1.2, mandatory in TLS 1.3. Static RSA / DH key exchange is forbidden. Intermediate keeps TLS 1.2 + 1.3; Modern locks out everything below 1.3 (highest assurance, drops older clients).">
                      <HelpOutlineIcon sx={{ fontSize: 13, color: colors.primary?.[300] || "#6371F7", cursor: "help" }} />
                    </Tooltip>
                  </Stack>
                }
                last={sslConfigForRender?.tls_policy_preset !== TlsProfile.CUSTOM}
                control={
                  <Select size="small" value={sslConfigForRender?.tls_policy_preset || TlsProfile.INTERMEDIATE}
                    onChange={(e) => onTlsProfile(e.target.value)} sx={miniSelectSx}>
                    <MenuItem value={TlsProfile.INTERMEDIATE}>Intermediate</MenuItem>
                    <MenuItem value={TlsProfile.MODERN}>Modern</MenuItem>
                    <MenuItem value={TlsProfile.CUSTOM}>Custom</MenuItem>
                  </Select>
                }
              />
              {sslConfigForRender?.tls_policy_preset === TlsProfile.CUSTOM && (
                <CustomTlsRows sslConfig={sslConfigForRender} onCustomTlsField={onCustomTlsField} disabled={disabled} />
              )}
            </SectionedCard>

            {/* Redirects */}
            <SectionedCard
              icon={<RedoIcon sx={{ fontSize: 14 }} />}
              title="Redirects & canonical URLs"
              desc="How Layerd rewrites incoming HTTP requests."
            >
              <SettingRow
                name="HTTP → HTTPS redirection"
                desc="Redirect plain HTTP requests to the secure equivalent (301)."
                control={<>
                  {sslConfigForRender?.https_redirect_enabled
                    ? <Pill tone="ok"><Box sx={dotSx} />Enabled</Pill>
                    : <Pill><Box sx={{ ...dotSx, backgroundColor: colors.neutral[500] }} />Disabled</Pill>}
                  <GreenSwitch checked={!!sslConfigForRender?.https_redirect_enabled} disabled={disabled}
                    onChange={(e) => onToggle("httpsRedirectChange", { enable: e.target.checked })} />
                </>}
              />
              <SettingRow
                name={<>Force <Box component="span" sx={ { fontSize: 12 }}>www</Box> redirect</>}
                desc="Always redirect apex → www (or vice versa)."
                control={<>
                  {sslConfigForRender?.www_redirect_enabled
                    ? <Pill tone="ok"><Box sx={dotSx} />Enabled</Pill>
                    : <Pill><Box sx={{ ...dotSx, backgroundColor: colors.neutral[500] }} />Disabled</Pill>}
                  <GreenSwitch checked={!!sslConfigForRender?.www_redirect_enabled} disabled={disabled}
                    onChange={(e) => onToggle("wwwRedirectChange", { enable: e.target.checked })} />
                </>}
              />
              <SettingRow
                name="Automatic HTTP rewrites"
                desc={<>Rewrite mixed-content URLs in HTML responses from <Box component="span" sx={ { fontSize: 12 }}>http://</Box> to <Box component="span" sx={ { fontSize: 12 }}>https://</Box>.</>}
                last
                control={<>
                  {sslConfigForRender?.http_rewrite_enabled
                    ? <Pill tone="ok"><Box sx={dotSx} />Enabled</Pill>
                    : <Pill><Box sx={{ ...dotSx, backgroundColor: colors.neutral[500] }} />Disabled</Pill>}
                  <GreenSwitch checked={!!sslConfigForRender?.http_rewrite_enabled} disabled={disabled}
                    onChange={(e) => onToggle("autoHttpRewriteChange", { enable: e.target.checked })} />
                </>}
              />
            </SectionedCard>

            {/* HSTS */}
            <SectionedCard
              icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
              title="HTTP Strict Transport Security (HSTS)"
              desc="Tells browsers to always use HTTPS for this domain."
              warning
            >
              <SettingRow
                name="Enable HSTS"
                desc="Browsers will refuse insecure connections to this site for the configured duration."
                control={<>
                  {hstsEnabled
                    ? <Pill tone="ok"><Box sx={dotSx} />Enabled</Pill>
                    : <Pill tone="off"><Box sx={dotSx} />Disabled</Pill>}
                  <GreenSwitch checked={hstsEnabled} disabled={disabled}
                    onChange={(e) => onToggle("hstsSettingChange", { enable: e.target.checked })} />
                </>}
              />
              <SettingRow
                muted={!hstsEnabled}
                name="Max age"
                desc="How long browsers should remember the HTTPS-only rule."
                control={
                  <Select size="small" value={sslConfigForRender?.hsts?.max_age || 15552000}
                    onChange={(e) => onHstsUpdate({ max_age: Number(e.target.value) })}
                    disabled={hstsChildDisabled}
                    sx={miniSelectSx}>
                    <MenuItem value={300}>5 minutes</MenuItem>
                    <MenuItem value={86400}>1 day</MenuItem>
                    <MenuItem value={604800}>1 week</MenuItem>
                    <MenuItem value={2592000}>1 month</MenuItem>
                    <MenuItem value={15552000}>6 months</MenuItem>
                    <MenuItem value={31536000}>1 year (preload)</MenuItem>
                  </Select>
                }
              />
              <SettingRow
                muted={!hstsEnabled}
                name="Include subdomains"
                desc={<>Apply HSTS to every subdomain of this site.</>}
                control={<>
                  {hstsEnabled && sslConfigForRender?.hsts?.include_sub_domains
                    ? <Pill tone="ok"><Box sx={dotSx} />Enabled</Pill>
                    : <Pill tone="off"><Box sx={dotSx} />Disabled</Pill>}
                  <GreenSwitch checked={!!sslConfigForRender?.hsts?.include_sub_domains}
                    disabled={hstsChildDisabled}
                    onChange={(e) => onHstsUpdate({ include_sub_domains: e.target.checked })} />
                </>}
              />
              <SettingRow
                muted={!hstsEnabled}
                name="Preload"
                desc="Submit this site to the HSTS preload list shipped with major browsers."
                control={<>
                  {hstsEnabled && sslConfigForRender?.hsts?.preload
                    ? <Pill tone="ok"><Box sx={dotSx} />Enabled</Pill>
                    : <Pill tone="off"><Box sx={dotSx} />Disabled</Pill>}
                  <GreenSwitch checked={!!sslConfigForRender?.hsts?.preload}
                    disabled={hstsChildDisabled}
                    onChange={(e) => onHstsUpdate({ preload: e.target.checked })} />
                </>}
              />
              <SettingRow
                muted={!hstsEnabled}
                name="No-Sniff header"
                desc={<>Send <Box component="span" sx={ { fontSize: 12 }}>X-Content-Type-Options: nosniff</Box> on all responses.</>}
                last
                control={<>
                  {hstsEnabled && sslConfigForRender?.hsts?.no_sniff_header
                    ? <Pill tone="ok"><Box sx={dotSx} />Enabled</Pill>
                    : <Pill tone="off"><Box sx={dotSx} />Disabled</Pill>}
                  <GreenSwitch checked={!!sslConfigForRender?.hsts?.no_sniff_header}
                    disabled={hstsChildDisabled}
                    onChange={(e) => onHstsUpdate({ no_sniff_header: e.target.checked })} />
                </>}
              />
            </SectionedCard>
          </Stack>

        <Stack
          spacing={INNER_STACK_SPACING}
          sx={{ width: { xs: "100%", lg: RIGHT_RAIL_WIDTH }, maxWidth: "100%", minWidth: 0 }}
        >
            <RecommendationsCard
              sslConfig={sslConfigForRender}
              sslRating={sslRating}
              onRescan={onRescan}
              onAction={onRecommendationAction}
            />
            <CertificateHistory sslConfig={sslConfigForRender} />
        </Stack>
      </Box>

      <StickySaveBar
        open={plainModifiedCount > 0}
        modifiedCount={plainModifiedCount}
        modifiedFields={plainModifiedFields}
        onDiscard={discardPlain}
        onApply={applyPlain}
        isApplying={isApplyingPlain}
      />

      <InstallCertDrawer
        open={installOpen}
        onClose={() => setInstallOpen(false)}
        siteUid={siteUid}
        initialTab={installTab}
      />

      <SslModeCompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        mode={compareMode}
        modeTitle={compareModeMeta?.title}
        modeDesc={compareModeMeta?.desc}
        onModeChange={setCompareMode}
      />
    </Stack>
  );
}
